import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Card, Divider, Button, TextField, Select, MenuItem, FormControl, FormControlLabel, InputLabel, Grid, Box, Modal, Tooltip, Autocomplete, Dialog, CircularProgress, DialogContent, DialogTitle, DialogContentText, DialogActions, Toolbar, Slider, Switch, Snackbar, Collapse } from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TradingViewChart from './TradingViewChart_v2';
import CustomDatafeed from './CustomDatafeed_v2'; 
import { db, collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, addDoc, updateDoc, onSnapshot, writeBatch } from '../firebaseConfig';
import { useTheme } from '@mui/material/styles';
import MDButton from "components/MDButton";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import FastForwardIcon from '@mui/icons-material/FastForward';
import Avatar from '@mui/material/Avatar';
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import { fetchAndStoreMarketData,fetchMarketData, } from './fetchAndStoreMarketData_v2'; // Adjust the import path as needed
import './FXBacktesting.css';
import PropFirmChallenge from './PropFirmChallenge';


const calculateProfitLoss = async (exitPrice, size, side, userID, currentSessionId, tradeNumber, contractSize) => {
  console.log('calculateProfitLoss - Parameters:', {
    exitPrice,
    size,
    side,
    userID,
    currentSessionId,
    tradeNumber,
    contractSize
  });

  if (!contractSize) {
    console.error('Contract size is undefined');
    return null;
  }

  const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
  const tradeRef = doc(sessionRef, 'trades', `Trade ${tradeNumber}`);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    console.error('Trade document does not exist.');
    return null;
  }

  const tradeData = tradeDoc.data();
  const exchangeRates = tradeData.cachedExchangeRates || {};
  const entryPrice = tradeData.price;

  if (!entryPrice) {
    console.error('Entry price could not be fetched from Firestore.');
    return null;
  }

  // Check if exchangeRates is empty
  if (Object.keys(exchangeRates).length === 0) {
    console.error('Exchange rates are empty');
    return null;
  }

  const exchangeRate = exchangeRates[Object.keys(exchangeRates)[0]];

  // Handle cases where exchangeRate might be undefined or zero
  if (exchangeRate === undefined || exchangeRate === 0) {
    console.error('Exchange rate is undefined or zero');
    return null;
  }

  // Calculate P&L in quote currency
  const profitLossInQuoteCurrency = (side === 'Buy' ? 
    (exitPrice - entryPrice) : 
    (entryPrice - exitPrice)) * size * contractSize;

  // Adjust for exchange rate
  const adjustedProfitLoss = exchangeRate === 1
    ? profitLossInQuoteCurrency
    : profitLossInQuoteCurrency / exchangeRate;

  console.log('Profit/Loss Calculation:', {
    entryPrice,
    exitPrice,
    size,
    contractSize,
    exchangeRate,
    profitLossInQuoteCurrency,
    adjustedProfitLoss
  });

  const finalProfitLoss = isNaN(adjustedProfitLoss) ? 0 : Number(adjustedProfitLoss.toFixed(2));
  return finalProfitLoss;
};

async function saveTradeToFirestore(trade, userID, currentSessionId, playbackTime) {
  console.log(`saveTradeToFirestore - userID: ${userID}, currentSessionId: ${currentSessionId}`);
  console.log('Parameters:');
  console.log('trade:', trade);
  console.log('userID:', userID);
  console.log('currentSessionId:', currentSessionId);
  console.log('playbackTime:', playbackTime);
  try {
    if (!userID || !currentSessionId) {
      throw new Error(`Missing userID or currentSessionId. userID: ${userID}, currentSessionId: ${currentSessionId}`);
    }
    // Ensure data types are correct
    if (typeof userID !== 'string' || typeof currentSessionId !== 'string') {
      throw new Error(`Invalid data types. userID: ${typeof userID}, currentSessionId: ${typeof currentSessionId}`);
    }

    if (typeof playbackTime !== 'number') {
      throw new Error(`Invalid data type for playbackTime. Expected number, got ${typeof playbackTime}`);
    }
    // Check if trade is defined and has tradeNumber
    if (!trade || !trade.tradeNumber) {
      throw new Error(`Trade is undefined or missing tradeNumber. trade: ${JSON.stringify(trade)}`);
    }
    const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
    const tradeRef = doc(sessionRef, 'trades', `Trade ${trade.tradeNumber}`);

    await updateDoc(tradeRef, {
      status: trade.status,
      entryPrice: trade.price,
    });

    await setDoc(tradeRef, {
      exitPrice: trade.exitPrice !== undefined ? trade.exitPrice : 0,
      profitLoss: trade.profitLoss !== undefined ? trade.profitLoss : 0,
      exitDate: typeof playbackTime === 'number' ? new Date(playbackTime).toISOString() : playbackTime,
      riskPercent: Number(trade.riskPercent), // Explicitly save riskPercent
    }, { merge: true });

    await updateDoc(sessionRef, {
      currentPlaybackTime: playbackTime,
      exitDate: typeof playbackTime === 'number' ? new Date(playbackTime).toISOString() : playbackTime,
    });

    console.log(`Trade ${trade.tradeNumber} updated in Firestore.`);
  } catch (error) {
    console.error(`Error updating trade in Firestore for trade ${trade.tradeNumber}. userID: ${userID}, currentSessionId: ${currentSessionId}, error: ${error.message}`);
  }
}

export const calculateUnrealizedProfit = (currentPrice, trade, contractSize) => {
  console.log('calculateUnrealizedProfit - Parameters:');
  console.log('currentPrice:', currentPrice);
  console.log('trade:', trade);

  const entryPrice = trade.price;
  const side = trade.side;
  const size = trade.size;
  const exchangeRates = trade.cachedExchangeRates || {};
  const symbol = trade.symbol;

  if (!entryPrice || !currentPrice) {
    console.error('Missing entryPrice or currentPrice');
    return 0;
  }

  // Check if exchangeRates is empty
  if (Object.keys(exchangeRates).length === 0) {
    console.error('Exchange rates are empty');
    return 0;
  }

  const exchangeRate = exchangeRates[Object.keys(exchangeRates)[0]];

  // Handle cases where exchangeRate might be undefined or zero
  if (exchangeRate === undefined || exchangeRate === 0) {
    console.error('Invalid exchange rate');
    return 0;
  }

  const profitLossInQuoteCurrency = (side === 'Buy' ? (currentPrice - entryPrice) : (entryPrice - currentPrice)) * size * contractSize;
  
  const adjustedProfitLoss = exchangeRate === 1
    ? profitLossInQuoteCurrency
    : profitLossInQuoteCurrency / exchangeRate;

  console.log('Unrealized Profit:', adjustedProfitLoss);
  return adjustedProfitLoss;
};

export const checkTakeProfitStopLoss = async (
  currentPrice, trade, highPrice, lowPrice, userID, currentSessionId, sessionId, playbackTime, contractSize,
  setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen
) => {
  if (trade.status === "Open") {
    if (trade.side === 'Sell') {
      if (trade.stopLoss !== 0 && highPrice >= trade.stopLoss) {
        trade.status = "Closed";
        trade.exitPrice = trade.stopLoss;
        trade.profitLoss = await calculateProfitLoss(trade.stopLoss, trade.size, trade.side, userID, currentSessionId, trade.tradeNumber, contractSize);
        trade.exitDate = typeof playbackTime === 'number' ? new Date(playbackTime).toISOString() : playbackTime;
        await saveTradeToFirestore(trade, userID, currentSessionId, playbackTime);
        setTradeSnackbarMessage(`Sell Trade #${trade.tradeNumber} closed at TP: ${trade.takeProfit}, P&L: $${trade.profitLoss.toFixed(2)}`);
        setTradeSnackbarColor('error');
        setTradeSnackbarOpen(true);
      } else if (trade.takeProfit !== 0 && lowPrice <= trade.takeProfit) {
        trade.status = "Closed";
        trade.exitPrice = trade.takeProfit;
        trade.profitLoss = await calculateProfitLoss(trade.takeProfit, trade.size, trade.side, userID, currentSessionId, trade.tradeNumber, contractSize);
        trade.exitDate = typeof playbackTime === 'number' ? new Date(playbackTime).toISOString() : playbackTime;
        await saveTradeToFirestore(trade, userID, currentSessionId, playbackTime);
        setTradeSnackbarMessage(`Sell Trade #${trade.tradeNumber} closed at TP: ${trade.takeProfit}, P&L: $${trade.profitLoss.toFixed(2)}`);
        setTradeSnackbarColor('success');
        setTradeSnackbarOpen(true);
      }
    } else if (trade.side === 'Buy') {
      if (trade.stopLoss !== 0 && lowPrice <= trade.stopLoss) {
        trade.status = "Closed";
        trade.exitPrice = trade.stopLoss;
        trade.profitLoss = await calculateProfitLoss(trade.stopLoss, trade.size, trade.side, userID, currentSessionId, trade.tradeNumber, contractSize);
        trade.exitDate = typeof playbackTime === 'number' ? new Date(playbackTime).toISOString() : playbackTime;
        await saveTradeToFirestore(trade, userID, currentSessionId, playbackTime);
        setTradeSnackbarMessage(`Buy Trade #${trade.tradeNumber} closed at TP: ${trade.takeProfit}, P&L: $${trade.profitLoss.toFixed(2)}`);
        setTradeSnackbarColor('error');
        setTradeSnackbarOpen(true);
      } else if (trade.takeProfit !== 0 && highPrice >= trade.takeProfit) {
        trade.status = "Closed";
        trade.exitPrice = trade.takeProfit;
        trade.profitLoss = await calculateProfitLoss(trade.takeProfit, trade.size, trade.side, userID, currentSessionId, trade.tradeNumber, contractSize);
        trade.exitDate = typeof playbackTime === 'number' ? new Date(playbackTime).toISOString() : playbackTime;
        await saveTradeToFirestore(trade, userID, currentSessionId, playbackTime);
        setTradeSnackbarMessage(`Buy Trade #${trade.tradeNumber} closed at TP: ${trade.takeProfit}, P&L: $${trade.profitLoss.toFixed(2)}`);
        setTradeSnackbarColor('success');
        setTradeSnackbarOpen(true);
      }
    }
  }
};

export const checkLimitOrderHit = async (
  currentPrice, trade, highPrice, lowPrice, userID, currentSessionId, sessionId, playbackTime, contractSize,
  setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen
) => {
  if (trade.status === "Pending") {
    if (trade.side === 'Buy') {
      if (highPrice >= trade.price && lowPrice <= trade.price) {
        trade.status = "Open";
        trade.entryPrice = trade.price;
        console.log(`Limit Order executed: ${trade.price}, new status: ${trade.status}`);
        await saveTradeToFirestore(trade, userID, currentSessionId, playbackTime);
        setTradeSnackbarMessage(`Buy Trade #${trade.tradeNumber} opened at ${trade.price}`);
        setTradeSnackbarColor('info');
        setTradeSnackbarOpen(true);
      }
    } else if (trade.side === 'Sell') {
      if (lowPrice <= trade.price && highPrice >= trade.price) {
        trade.status = "Open";
        trade.entryPrice = trade.price;
        console.log(`Limit Order executed: ${trade.price}, new status: ${trade.status}`);
        await saveTradeToFirestore(trade, userID, currentSessionId, playbackTime);
        setTradeSnackbarMessage(`Sell Trade #${trade.tradeNumber} opened at ${trade.price}`);
        setTradeSnackbarColor('info');
        setTradeSnackbarOpen(true);
      }
    }
  }
};
export const updateTrades = (trades, updatedTrade, userID, currentSessionId) => {
  return trades.map(trade => trade.id === updatedTrade.id ? updatedTrade : trade);
};

const timeframes = [
  { value: '1', label: '1 Minute' },
  { value: '3', label: '3 Minutes' },
  { value: '5', label: '5 Minutes' },
  { value: '10', label: '10 Minutes' },
  { value: '15', label: '15 Minutes' },
  { value: '30', label: '30 Minutes' },
  { value: '60', label: '1 Hour' },
  { value: '240', label: '4 Hours' },
  { value: 'D', label: 'Daily' },
];

const symbols = [
  { value: 'AAPL', label: 'AAPL'},
  { value: 'MSFT', label: 'MSFT' }, // Microsoft
  { value: 'GOOGL', label: 'GOOGL' }, // Alphabet (Google)
  { value: 'AMZN', label: 'AMZN' }, // Amazon
  { value: 'TSLA', label: 'TSLA' }, // Tesla
  { value: 'NVDA', label: 'NVDA' }, // NVIDIA
  { value: 'JPM', label: 'JPM' }, // JPMorgan Chase
  { value: 'WMT', label: 'WMT' }, // Walmart
  { value: 'EURUSD', label: 'EURUSD' },
  { value: 'GBPUSD', label: 'GBPUSD' },
  { value: 'USDJPY', label: 'USDJPY' },
  { value: 'AUDUSD', label: 'AUDUSD' },
  { value: 'USDCHF', label: 'USDCHF' },
  { value: 'USDCAD', label: 'USDCAD' },
  { value: 'AUDCAD', label: 'AUDCAD' },
  { value: 'AUDNZD', label: 'AUDNZD' },
  { value: 'NZDUSD', label: 'NZDUSD' },
  { value: 'GBPJPY', label: 'GBPJPY' },
  { value: 'AUDJPY', label: 'AUDJPY' },
  { value: 'EURGBP', label: 'EURGBP' },
  { value: 'EURAUD', label: 'EURAUD' },
  { value: 'GBPAUD', label: 'GBPAUD' },
  { value: 'GBPCHF', label: 'GBPCHF' },
  { value: 'EURCHF', label: 'EURCHF' },
  { value: 'NZDCAD', label: 'NZDCAD' },
  { value: 'NZDJPY', label: 'NZDJPY' },
  { value: 'CADJPY', label: 'CADJPY' },
  { value: 'CHFJPY', label: 'CHFJPY' },
  { value: 'XAUUSD', label: 'XAUUSD' }, // Commodity
  { value: 'XAGUSD', label: 'XAGUSD' }, // Commodity
  { value: 'WTIUSD', label: 'WTIUSD' }, // Commodity
  { value: 'BRENTUSD', label: 'BRENTUSD' }, // Commodity
  { value: 'COPPERUSD', label: 'COPPERUSD' }, // Commodity
  { value: 'SUGARUSD', label: 'SUGARUSD' }, // Commodity
  { value: 'NGASUSD', label: 'NGASUSD' }, // Commodity
  // { value: 'GOLD', label: 'GOLD' }, // Commodity
  // { value: 'SILVER', label: 'SILVER' }, // Commodity
  // { value: 'CRUDE_OIL', label: 'CRUDE OIL' }, // Commodity
  // { value: 'BRENT_OIL', label: 'BRENT OIL' }, // Commodity
  // { value: 'NATURAL_GAS', label: 'NATURAL GAS' }, // Commodity
  // { value: 'COPPER', label: 'COPPER' }, // Commodity
  // { value: 'PLATINUM', label: 'PLATINUM' }, // Commodity
  // { value: 'PALLADIUM', label: 'PALLADIUM' }, // Commodity
  // { value: 'COFFEE', label: 'COFFEE' }, // Commodity
  // { value: 'CORN', label: 'CORN' }, // Commodity
  // { value: 'WHEAT', label: 'WHEAT' }, // Commodity
  // { value: 'SOYBEAN', label: 'SOYBEAN' }, // Commodity
  // { value: 'COTTON', label: 'COTTON' }, // Commodity
  // { value: 'SUGAR', label: 'SUGAR' }, // Commodity
  // { value: 'COCOA', label: 'COCOA' }, // Commodity
];


const FXBacktesting = ({ userID, isDemo }) => {
  const [data, setData] = useState([]);
  const [symbol, setSymbol] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('60');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [exchangeRateData, setExchangeRateData] = useState({});
  const [orderSideError, setOrderSideError] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({});
  const [notificationColor, setNotificationColor] = useState('');
  const [orderTypeError, setOrderTypeError] = useState(false);
  const [entryPriceError, setEntryPriceError] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [trade, setTrade] = useState(null);
  const [profitLoss, setprofitLoss] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(null);
  const [forceRender, setForceRender] = useState(false); // State to force re-render
  const [sessionId, setSessionId] = useState('');
  const [customStopLoss, setCustomStopLoss] = useState(null);
  const [customTakeProfit, setCustomTakeProfit] = useState(null);
  const [customRiskAmount, setCustomRiskAmount] = useState(null);
  const [exitDate, setExitDate] = useState(null);
  const [totalUnrealizedPnL, setTotalUnrealizedPnL] = useState(0);
  const [customRrTarget, setCustomRrTarget] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [tradeSnackbarOpen, setTradeSnackbarOpen] = useState(false);
  const [tradeSnackbarMessage, setTradeSnackbarMessage] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentCandleIndex, setCurrentCandleIndex] = useState(0);
  const [calculatedEntryPrice, setCalculatedEntryPrice] = useState (0);
  const [cachedCandlePrice, setCachedCandlePrice] = useState(null); // State to cache the candle price
  const [openChallengeModal, setOpenChallengeModal] = useState(false);
  const [estimatedProfit, setEstimatedProfit] = useState (0);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [reallizedPnL, setReallizedPnL] = useState(0);  
  // Update initial state to remove default
  const [editTradeData, setEditTradeData] = useState({
    stopLoss: '',
    takeProfit: '',
    price: '',
    trailingStopLoss: false,
    trailingStopLossType: '', // Remove default value
    trailingStopLossValue: '',
    partialTakeProfit: false,
    partialTakeProfitPercent: 0,
  });
  const [isEditTradeModalOpen, setIsEditTradeModalOpen] = useState(false);
  const [trailingStopLoss, setTrailingStopLoss] = useState(false);
  const [trailingStopLossPercent, setTrailingStopLossPercent] = useState(0);
  const [partialTakeProfitPercent, setPartialTakeProfitPercent] = useState(0);
  const [estimatedLoss, setEstimatedLoss] = useState (0);
  const [symbolData, setSymbolData] = useState ();
  const [orders, setOrders] = useState([]);
  const [chartLayout, setChartLayout] = useState(null);
  const [positions, setPositions] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [lastTrade, setLastTrade] = useState(null); // State for the last trade
  const intervalRef = useRef(null);
  const [datafeed, setDatafeed] = useState(null); 
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [positionSize, setPositionSize] = useState();
  const [tradeHistory, setTradeHistory] = useState([]);
  const [accountBalance, setAccountBalance] = useState(0); 
  const [cachedExchangeRates, setCachedExchangeRates] = useState(null);
  const [riskPercent, setRiskPercent] = useState(1); // Default risk percent
  const [amount, setAmount] = useState(); // Default amount
  const [entryPrice, setEntryPrice] = useState(''); // Default entry price
  const [exitPrice, setExitPrice] = useState(''); // Default exit price
  const [stopLoss, setStopLoss] = useState(0); // Default stop loss
  const [isEditAction, setIsEditAction] = useState(false);
  const [takeProfit, setTakeProfit] = useState(0); // Default take profit
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [rrTarget, setRrTarget] = useState(2);
  const [side, setSide] = useState('buy'); // Default side
  const [exchangeRate, setExchangeRate] = useState(1);
  const [useLots, setUseLots] = useState(true);  // Toggle between lots and dollar amount
  const [trades, setTrades] = useState([]); // Initialize trades state
  const [orderType, setOrderType] = useState("market");
  const [orderSide, setOrderSide] = useState("buy");  // Toggle between buy and sell
  const [useStopLossTakeProfit, setUseStopLossTakeProfit] = useState(true);
  const [snackbarColor, setSnackbarColor] = useState(''); // Default color
  const [performanceStats, setPerformanceStats] = useState({
    winRate: 0,
    avgRiskReward: 0,
    profitLoss: 0,
    maxDrawdown: 0,
  });
  const [customTimeframe, setCustomTimeframe] = useState('60');
  const [currentBarIndex, setCurrentBarIndex] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [statusNotification, setStatusNotification] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState();
  const [preferences, setPreferences] = useState({});
  const [settings, setSettings] = useState({
    theme: 'dark',
    upColor: 'orange',
    downColor: 'black',
    backgroundColor: '#ffffff',
    wickUpColor: 'black',
    wickDownColor: 'black',
  });
  const [startDate, setStartDate] = useState('');
  const [startDateMillis, setStartDateMillis] = useState('');
  const [tradeSnackbarColor, setTradeSnackbarColor] = useState('');
  const [endDateMillis, setEndDateMillis] = useState('');
  const theme = useTheme();
  const [endDate, setEndDate] = useState('');
  const [currentBar, setCurrentBar] = useState({});
  const datafeedRef = useRef(null); // Declare datafeedRef
  const [isNewSessionModalOpen, setIsNewSessionModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [calculatedSL, setCalculatedSL] = useState(null);
  const [calculatedTP, setCalculatedTP] = useState(null);
  const [useManualTpSl, setUseManualTpSl] = useState(false);
  const [unrealizedPnL, setUnrealizedPnL] = useState(0);
  const [sessions, setSessions] = useState([]); // Array to hold previous sessions
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [previousTradeHistory, setPreviousTradeHistory] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [unrealizedProfit, setUnrealizedProfit] = useState(0);
  const [realizedPnL, setRealizedPnL] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [displayStopLoss, setDisplayStopLoss] = useState(stopLoss);
  const [selectedChallenges, setSelectedChallenges] = useState([]);
  const [currentCandlePrice, setCurrentCandlePrice] = useState(null);
  let isFetchingExchangeRates = false;
  const [currentPrice, setCurrentPrice] = useState(0); // Initialize currentPrice state
  const [interval, setInterval] = useState('15');
  const [drawingToolValues, setDrawingToolValues] = useState(null);
  const [modalOpenedByDrawing, setModalOpenedByDrawing] = useState(false); // New flag
  const [modalContent, setModalContent] = useState('');
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const [newSession, setNewSession] = useState({
    accountBalance: '',
    symbol: '',
    startDate: '',
    endDate: '',
    strategy: '',
    sessionName: '',
    createdOn: new Date().toISOString(), // Add session name
  });
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    backgroundColor: theme.palette.background.default || '#bac7db',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
}; 


  useEffect(() => {
    console.log('FXBacktesting - sessionId:', sessionId);
    console.log('Type of sessionId in FXBacktesting:', typeof sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (isPlaying && data.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentCandleIndex(prevIndex => {
          if (prevIndex < data.length - 1) {
            const newIndex = prevIndex + 1;
            const candle = data[newIndex];
            if (candle && candle.time) {
              setCurrentPlaybackTime(candle.time * 1000);
              console.log('Updated currentPlaybackTime:', candle.time * 1000);
            }
            return newIndex;
          } else {
            clearInterval(intervalRef.current);
            setIsPlaying(false);
            return prevIndex;
          }
        });
      }, 1000 / playbackSpeed);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, playbackSpeed, data]);

  // Function to update currentPlaybackTime based on currentCandleIndex
  const updateCurrentPlaybackTime = useCallback(() => {
    if (data.length > 0 && currentCandleIndex >= 0 && currentCandleIndex < data.length) {
      const candle = data[currentCandleIndex];
      if (candle && candle.time) {
        const newTime = candle.time * 1000;
        setCurrentPlaybackTime(newTime);
        console.log('updateCurrentPlaybackTime - Set currentPlaybackTime to:', newTime);
        return newTime;
      }
    }
    console.log('updateCurrentPlaybackTime - No valid candle data, keeping currentPlaybackTime:', currentPlaybackTime);
    return currentPlaybackTime;
  }, [data, currentCandleIndex, currentPlaybackTime]);

    // Modified handleOpenStageDialog to update currentPlaybackTime
    const handleOpenStageDialog = useCallback((challengeId, nextStage) => {
      updateCurrentPlaybackTime();
      setTimeout(() => {
        setStageDialog({ open: true, challengeId, nextStage });
      }, 100); // Small delay to ensure state update
    }, [updateCurrentPlaybackTime]);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const docRef = doc(db, 'Users', userID, 'preferences', 'chartSettings');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings(docSnap.data());
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };

    loadPreferences();
  }, [userID]);

// In FXBacktesting.js
useEffect(() => {
  if (!currentSessionId || !isSessionActive) return;

  const tradesRef = collection(db, 'Users', userID, 'backtesting', currentSessionId, 'trades');
  const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);

  const unsubscribe = onSnapshot(tradesRef, async (snapshot) => {
    const updatedTrades = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Handle notifications (unchanged)
    updatedTrades.forEach((trade) => {
      const existingTrade = tradeHistory.find(t => t.id === trade.id);
      if (existingTrade && existingTrade.status !== trade.status) {
        setStatusNotification(`Trade #${trade.id} status changed to ${trade.status}`);
        setNotificationColor('#FF9800');
        setTimeout(() => {
          setStatusNotification('');
          setNotificationColor('');
        }, 3000);
      } else if (trade.status === 'Closed') {
        const profitLossMessage = `Trade #${trade.id} status changed to ${trade.status} with profit/loss: $${parseFloat(trade.profitLoss).toFixed(2)}`;
        setStatusNotification(profitLossMessage);
        setNotificationColor(trade.profitLoss >= 0 ? '#4CAF50' : '#F44336');
        setTimeout(() => {
          setStatusNotification('');
          setNotificationColor('');
        }, 3000);
      }
    });

    setTradeHistory(updatedTrades);
    setHasLoaded(true);

// Fetch initial balance from session document
const sessionSnap = await getDoc(sessionRef);
const initialBalance = Number(sessionSnap.data().initialAccountBalance || 10000); // Use initialAccountBalance

// Calculate realized PnL
const totalRealizedPnL = updatedTrades
  .filter(trade => trade.status === 'Closed')
  .reduce((sum, trade) => sum + (trade.profitLoss || 0), 0);
setRealizedPnL(totalRealizedPnL);

// Calculate and set account balance
const currentAccountBalance = initialBalance + totalRealizedPnL;
setAccountBalance(currentAccountBalance);

// Update session document with current account balance
await updateDoc(sessionRef, { accountBalance: currentAccountBalance });

// Calculate unrealized PnL
const totalUnrealizedPnL = updatedTrades
  .filter(trade => trade.status === 'Open')
  .reduce((sum, trade) => sum + (trade.unrealizedProfit || 0), 0);
setTotalUnrealizedPnL(totalUnrealizedPnL);
});

return () => unsubscribe();
}, [currentSessionId, isSessionActive, userID]);

  useEffect(() => {
    const fetchSessionsWithPnL = async () => {
      try {
        // Fetch previous backtesting sessions from Firebase
        const sessionsRef = collection(db, 'Users', userID, 'backtesting');
        const sessionsSnapshot = await getDocs(sessionsRef);
        const sessionsData = sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
        // Calculate total PnL for each session
        const sessionsWithPnL = await Promise.all(sessionsData.map(async (session) => {
          const totalPnL = await calculateSessionTotalPnL(userID, session.id);
          return { ...session, totalPnL };
        }));
  
        // Set the sessions with total PnL
        setSessions(sessionsWithPnL);
      } catch (error) {
        console.error('Error fetching previous sessions:', error);
      }
    };
  
    fetchSessionsWithPnL();
  }, [userID, isSessionActive]); // Add `isSessionActive` as a dependency


  const savePreferences = async (newSettings) => {
    try {
      const docRef = doc(db, 'Users', userID, 'preferences', 'chartSettings');
      await setDoc(docRef, newSettings);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };
  
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };
  const formatDateToMidnight = (date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
        throw new Error(`Invalid date value: ${date}`);
    }
    d.setUTCHours(0, 0, 0, 0); // Use UTC for consistency
    return d.toISOString();
  };

  // Add a button to trigger the Prop Firm Challenge modal
const handleOpenChallengeModal = () => {
  setOpenChallengeModal(true);
};

// Ensure the modal close resets the state
const handleCloseChallengeModal = () => {
  setOpenChallengeModal(false);
};

  const getTimestampInMillis = (date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
        throw new Error(`Invalid date value: ${date}`);
    }
    return d.getTime(); // Returns timestamp in milliseconds
  };
  useEffect(() => {
    if (newSession.startDate) {
      setStartDateMillis(getTimestampInMillis(newSession.startDate));
    }
    if (newSession.endDate) {
      setEndDateMillis(getTimestampInMillis(newSession.endDate));
    }
  }, [newSession.startDate, newSession.endDate]);  

  const checkDemoRestrictions = async (userID) => {
    try {
      // Get user document to verify demo status
      const userDoc = await getDoc(doc(db, 'Users', userID));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      if (!userData.isDemo) {
        return true; // Not a demo account, proceed
      }
  
      // Check existing sessions for demo account
      const sessionsRef = collection(db, 'Users', userID, 'backtesting');
      const snapshot = await getDocs(sessionsRef);
      
      if (snapshot.size >= 1) {
        setSnackbarMessage("Demo accounts are limited to 1 backtesting session");
        setSnackbarColor('error');
        setShowSnackbar(true);
        return false;
      }
  
      return true;
    } catch (error) {
      console.error("Error checking demo restrictions:", error);
      return false;
    }
  };

  const validateDemoSession = async (startDate, endDate) => {
    if (!startDate || !endDate) return true; // Skip validation if dates not set yet
    
    // Check date range
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const diffTime = Math.abs(endDateObj - startDateObj);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 30) {
      return "Demo accounts are limited to 30 days of historical data";
    }
  
    // Check existing sessions
    const sessionsRef = collection(db, 'Users', userID, 'backtesting');
    const snapshot = await getDocs(sessionsRef);
    
    if (snapshot.size >= 1) {
      return "Demo accounts are limited to 1 backtesting session";
    }
  
    return true;
  };
  
  // Update handleNewSession
  const handleNewSession = async () => {
    try {
      if (!newSession.startDate || !newSession.endDate) {
        throw new Error('Start date or end date is missing');
      }
  
      const startDateObj = new Date(newSession.startDate);
      const endDateObj = new Date(newSession.endDate);
      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
        throw new Error('Invalid start or end date format');
      }
      if (startDateObj >= endDateObj) {
        throw new Error('Start date must be before end date');
      }
  
      if (isDemo) {
        const validationResult = await validateDemoSession(newSession.startDate, newSession.endDate);
        if (validationResult !== true) {
          setSnackbarMessage(validationResult);
          setSnackbarColor('error');
          setShowSnackbar(true);
          return;
        }
      }
  
      const startDateISO = formatDateToMidnight(newSession.startDate);
      const endDateISO = formatDateToMidnight(newSession.endDate);
      const startDateMillis = getTimestampInMillis(newSession.startDate);
      const endDateMillis = getTimestampInMillis(newSession.endDate);
  
      const sessionData = {
        sessionName: newSession.sessionName,
        startDate: startDateISO,
        endDate: endDateISO,
        startDateMillis,
        endDateMillis,
        symbol: newSession.symbol.label || newSession.symbol,
        initialAccountBalance: Number(newSession.accountBalance), // Fixed initial balance
        accountBalance: Number(newSession.accountBalance),
        createdOn: new Date().toISOString(),
        profitLoss: 0,
        strategy: newSession.strategy || 'N/A',
        isDemo: isDemo || false,
        currentPlaybackTime: startDateMillis, // Initialize with session start time
      };
  
      console.log('New session data:', sessionData);
  
      const newSessionRef = await addDoc(collection(db, 'Users', userID, 'backtesting'), sessionData);
      console.log('New session created with ID:', newSessionRef.id);
  
      setIsNewSessionModalOpen(false);
      setCurrentSessionId(newSessionRef.id);
      setIsSessionActive(true);
      setNewSession({ ...sessionData, id: newSessionRef.id });
      setSymbol(sessionData.symbol);
      setStartDate(sessionData.startDate);
      setCurrentPlaybackTime(startDateMillis); // Initialize currentPlaybackTime
      setEndDate(sessionData.endDate);
      setSelectedChallenges([]);
  
      // Use latest signature with userID and sessionId
      await fetchAndStoreMarketData(
        userID,
        newSessionRef.id,
        sessionData.symbol,
        '240',
        startDateMillis,
        endDateMillis
      );
  
      setSnackbarMessage(isDemo ? "Demo session created successfully" : "Session created successfully");
      setSnackbarColor('success');
      setShowSnackbar(true);
    } catch (error) {
      console.error('Error creating new session:', error);
      setSnackbarMessage(error.message || 'Failed to create session');
      setSnackbarColor('error');
      setShowSnackbar(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSession(prevState => ({
        ...prevState,
        [name]: value,
    }));
  };
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const maxDate = yesterday.toISOString().split('T')[0];

  const handleDatafeedReady = (datafeedInstance) => {
    setDatafeed(datafeedInstance); // Store the datafeed instance
  };

  const saveChartLayout = async (layout) => {
    try {
      if (!layout) {
        console.error('No layout data provided');
        return;
      }
  
      // Save layout directly without checking for charts property
      const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
      await updateDoc(sessionRef, {
        chartLayout: layout
      });
      
      console.log('Layout saved to database:', layout);
    } catch (error) {
      console.error('Error saving layout:', error);
    }
  };
  
  // Also update loadLayout function
  const loadLayout = async () => {
    try {
      const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
      const sessionSnapshot = await getDoc(sessionRef);
      
      if (sessionSnapshot.exists()) {
        const data = sessionSnapshot.data();
        if (data.chartLayout) {
          console.log('Found saved layout:', data.chartLayout);
          return data.chartLayout;
        }
      }
      console.log('No saved layout found');
      return null;
    } catch (error) {
      console.error('Error loading layout:', error);
      return null;
    }
  };

  const handleEndSession = async () => {
    console.log('CustomDatafeed instance:', datafeed); 
    try {
        console.log('Ending session:', newSession); // Debugging log
        // Check if userID and currentSessionId are not null
        if (!userID || !currentSessionId || !datafeed) {
            console.error('Missing userID, currentSessionId, or datafeed.');
            return;
        }
        
        // Call storePlaybackTime on the CustomDatafeed instance
        if (datafeed) {
          datafeed.storePlaybackTime();
        } else {
          console.error('datafeed is null');
          return;
        }

        // Retrieve the existing session to ensure we don't overwrite 'createdOn'
        const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
        console.log('Session reference:', sessionRef); // Debugging log
        const sessionSnapshot = await getDoc(sessionRef);

        if (sessionSnapshot.exists()) {
            // Get the current session data (including createdOn)
            const existingSessionData = sessionSnapshot.data();

            // Prepare the updated session data
            const sessionData = {
                ...existingSessionData,  // Retain all fields from the existing session, including 'createdOn'
                tradeHistory,
                performanceStats,
                settings,
                symbol,
                startDate: newSession.startDate,  // Update only fields that need to change
                endDate: newSession.endDate,
                strategy: newSession.strategy || 'N/A',
                accountBalance: newSession.accountBalance,
                sessionName: newSession.sessionName,
                riskPercent, // Include riskPercent
                entryPrice, // Include entryPrice
                stopLoss, // Include stopLoss
                takeProfit, // Include takeProfit
                side, // Include side
                orderType, // Include orderType
                playbackTimes: datafeed.playbackTimes, // Include playbackTimes
                currentPlaybackTime: datafeed.currentPlaybackTime,
            };

            // Save the updated session
            await setDoc(sessionRef, sessionData);
            console.log('Session updated:', sessionData);

            // Clear the current session state
            setTradeHistory([]);
            setPerformanceStats({
                winRate: 0,
                avgRiskReward: 0,
                profitLoss: 0,
                maxDrawdown: 0,
            });
            setSettings({
                theme: 'dark',
                upColor: 'orange',
                downColor: 'black',
                backgroundColor: '#ffffff',
                wickUpColor: 'black',
                wickDownColor: 'black',
            });
            setSymbol('AAPL');
            setStartDate('');
            setEndDate('');
            setNewSession({
                accountBalance: '',
                symbol: '',
                startDate: '',
                endDate: '',
                strategy: '',
            });

            // Set session as inactive
            setIsSessionActive(false);
            displayNotification('Session ended and saved successfully.');
        } else {
            console.error('No session found to update.');
        }
    } catch (error) {
        console.error('Error ending session:', error);
        displayNotification('Error ending session.');
    }
  };

  const handleMenuOpen = (event, session) => {
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSession(null);
  };
  
  const handleMenuAction = async (action) => {
    if (action === 'load') {
      // Load the session
      setNewSession(selectedSession);
      setSymbol(selectedSession.symbol);
      setStartDate(selectedSession.startDate);
      setEndDate(selectedSession.endDate);
      setCurrentSessionId(selectedSession.id); // Set the current session ID
      // Load other session details as needed
      setIsSessionActive(true);
      handleMenuClose();
    } else if (action === 'delete') {
      handleDialogOpen();
    }
  };

  const handleTradeMenuOpen = (event, trade) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrade(trade);
  };
  
  const handleTradeMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleTradeMenuAction = (action, trade) => {
    console.log(`Action: ${action}, Trade passed to menu action:`, trade);
  
    if (!trade) {
      console.error('No trade provided to handleTradeMenuAction');
      return;
    }
  
    switch (action) {
      case 'edit':
        setIsEditAction(true);
        setSelectedTrade(trade);
        break;
  
      case 'close':
        try {
          // Ensure trade has a symbol, fallback to current symbol if needed
          const tradeSymbol = trade.symbol || symbol;
          if (!tradeSymbol) {
            throw new Error('No symbol found for trade');
          }
  
          // Get and validate contract size
          const contractSize = getContractSize(tradeSymbol);
          console.log(`Determined contract size for ${tradeSymbol}:`, contractSize);
  
          // Validate datafeed exists and has current playback time
          if (!datafeed || !datafeed.currentPlaybackTime) {
            throw new Error('Datafeed not initialized or missing playback time');
          }
  
          closeTrade(
            trade,
            datafeed.currentPlaybackTime,
            contractSize
          );
        } catch (error) {
          console.error('Error closing trade:', error);
          // Could add snackbar notification here
        }
        break;
  
      case 'cancel':
        cancelTrade(trade);
        break;
  
      default:
        console.warn(`Unhandled action: ${action}`);
        break;
    }
    
    handleTradeMenuClose();
  };


  
  const handleDeleteSession = async () => {
    setIsLoading(true);
    try {
        // Delete intervals subcollection
        const intervalsCollectionRef = collection(db, 'Users', userID, 'backtesting', selectedSession.id, 'intervals');
        const intervalsSnapshot = await getDocs(intervalsCollectionRef);
        const deleteIntervalsPromises = intervalsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deleteIntervalsPromises);

        // Delete trades subcollection
        const tradesCollectionRef = collection(db, 'Users', userID, 'backtesting', selectedSession.id, 'trades');
        const tradesSnapshot = await getDocs(tradesCollectionRef);
        const deleteTradesPromises = tradesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deleteTradesPromises);

        // Delete the session document
        await deleteDoc(doc(db, 'Users', userID, 'backtesting', selectedSession.id));

        // Remove the session from the state
        setSessions(sessions.filter(s => s.id !== selectedSession.id));
    } catch (error) {
        console.error('Error deleting session:', error);
    } finally {
        setIsLoading(false);
        handleDialogClose();
        handleMenuClose();
    }
  };

  const handleLoadSession = async (sessionId) => {
    try {
        console.log('Loading session with ID:', sessionId); // Debugging log
        // Check if userID and sessionId are not null
        if (!userID || !sessionId) {
            console.error('userID or sessionId is null:', { userID, sessionId });
            return;
        }

        // Retrieve the existing session
        const sessionRef = doc(db, 'Users', userID, 'backtesting', sessionId);
        console.log('Session reference:', sessionRef); // Debugging log
        const sessionSnapshot = await getDoc(sessionRef);

        if (sessionSnapshot.exists()) {
            // Get the session data
            const sessionData = sessionSnapshot.data();
            console.log('Loaded session data:', sessionData);

            // Set the session data to state
            setCurrentSessionId(sessionId);
            setIsSessionActive(true);
            setNewSession(sessionData);
            setSymbol(sessionData.symbol);
            setStartDate(sessionData.startDate);
            setEndDate(sessionData.endDate);
            setAccountBalance(Number(sessionData.accountBalance)); // Use Firebase value directly
            setTradeHistory(sessionData.tradeHistory || []);
            setSelectedChallenges([]);
            setPerformanceStats(sessionData.performanceStats || {
                winRate: 0,
                avgRiskReward: 0,
                profitLoss: 0,
                maxDrawdown: 0,
            });
            setSettings(sessionData.settings || {
                theme: 'dark',
                upColor: 'orange',
                downColor: 'black',
                backgroundColor: '#ffffff',
                wickUpColor: 'black',
                wickDownColor: 'black',
            });

            // Fetch market data with the properly formatted start/end dates
            await fetchAndStoreMarketData(sessionData.symbol, '4h', sessionData.startDate, sessionData.endDate);
        } else {
            console.error('No session found to load.');
            displayNotification('No session found to load.');
        }
    } catch (error) {
        console.error('Error loading session:', error);
        displayNotification('Error loading session.');
    }
  };

// Update handleOpenNewSessionModal
const handleOpenNewSessionModal = async () => {
  const canProceed = await checkDemoRestrictions(userID);
  if (!canProceed) {
    return;
  }
  setIsNewSessionModalOpen(true);
};

  const handleCloseNewSessionModal = () => {
    setIsNewSessionModalOpen(false);
  };

  const handleDrawingToolClick = (entryValue, stopLossValue, takeProfitValue, drawingId) => {
    console.log('handleDrawingToolClick called with values:', { entryValue, stopLossValue, takeProfitValue, drawingId });
    if (entryValue && stopLossValue && takeProfitValue && drawingId) {
      console.log('Setting drawing tool values');
      const riskAmount = calculateRiskPercent(entryValue, stopLossValue, accountBalance); // Calculate risk percent
      setDrawingToolValues({
        entryValue: Number(entryValue),
        stopLossValue: Number(stopLossValue),
        takeProfitValue: Number(takeProfitValue),
        drawingId: drawingId
      });
      setOrderType('limit');
      setUseManualTpSl(true);
      setModalOpenedByDrawing(true);
      setCustomRiskAmount(riskAmount !== null ? riskAmount : 1); // Set default risk percent
    }
  };
// Helper function to calculate risk percent based on stop loss
const calculateRiskPercent = (entryPrice, stopLossPrice, accountBalance) => {
  if (!entryPrice || !stopLossPrice || !accountBalance) {
    console.warn('Missing values for risk calculation:', { entryPrice, stopLossPrice, accountBalance });
    return null;
  }
  const riskPerUnit = Math.abs(entryPrice - stopLossPrice);
  const positionSize = 1; // Assume 1 lot for simplicity; adjust based on your logic
  const contractSize = getContractSize(symbol); // Assume getContractSize is defined
  const riskAmount = riskPerUnit * positionSize * contractSize;
  const riskPercent = (riskAmount / accountBalance) * 100;
  return Number(riskPercent.toFixed(2));
};

useEffect(() => {
    // Only trigger modal when the values are available and it was opened by drawing tool
    if (modalOpenedByDrawing && drawingToolValues && drawingToolValues.entryValue && drawingToolValues.stopLossValue && drawingToolValues.takeProfitValue) {
        console.log('Drawing tool values updated:', drawingToolValues);
        setTimeout(() => {
            console.log('Opening order modal with drawing tool values');
            handleIsOrderModalOpen(true, drawingToolValues, drawingToolValues.drawingId); // Pass the drawingId here
        }, 300); // Adjust the delay as necessary
    }
}, [drawingToolValues, modalOpenedByDrawing]);

const handleIsOrderModalOpen = (open, values = null, drawingId = null) => {
    console.log('handleIsOrderModalOpen called with open:', open);
    console.log('Received drawing tool values:', values);
    console.log('Received drawing ID:', drawingId);

    if (values) {
        console.log('Setting drawing tool values in handleIsOrderModalOpen');
        setDrawingToolValues(prevValues => {
            if (prevValues?.entryValue === values.entryValue &&
                prevValues?.stopLossValue === values.stopLossValue &&
                prevValues?.takeProfitValue === values.takeProfitValue &&
                prevValues?.drawingId === drawingId) {
                console.log('Drawing tool values are the same, not updating state');
                return prevValues;
            }
            return {
                entryValue: Number(values.entryValue),
                stopLossValue: Number(values.stopLossValue),
                takeProfitValue: Number(values.takeProfitValue),
                drawingId: drawingId // Store the drawing ID
            };
        });
        setOrderType('limit'); // Set order type to limit when drawing is clicked
        setUseManualTpSl(true); // Set to manual TP/SL when drawing is clicked
        setModalOpenedByDrawing(true); // Set flag to true
    } else {
        console.log('Resetting order type and TP/SL in handleIsOrderModalOpen');
        setOrderType('market'); // Set default order type to market
        setUseManualTpSl(false); // Set default to auto TP/SL
        setModalOpenedByDrawing(false); // Reset flag
        setDrawingToolValues(null); // Clear drawing values when modal is closed
    }

    if (isOrderModalOpen !== open) {
        console.log('Setting isOrderModalOpen to:', open);
        setIsOrderModalOpen(open);
    }
    if (!open) {
        console.log('Modal closed, resetting modalOpenedByDrawing');
        setModalOpenedByDrawing(false); // Ensure loop breaks when modal is closed
    }
};

useEffect(() => {
    if (isOrderModalOpen) {
        console.log('Order modal opened');
        setOrderType('market'); // Set default order type to market
        setUseManualTpSl(false); // Set default to auto TP/SL
    } else {
        console.log('Order modal closed, resetting modalOpenedByDrawing');
        setModalOpenedByDrawing(false); // Reset flag when modal is closed
    }
}, [isOrderModalOpen]);

useEffect(() => {
    if (drawingToolValues) {
        const { entryValue, stopLossValue, drawingId } = drawingToolValues;
        console.log('Setting order side based on drawing tool values');
        console.log('Drawing ID:', drawingId);
        if (entryValue > stopLossValue) {
            setOrderSide('buy');
        } else if (stopLossValue > entryValue) {
            setOrderSide('sell');
        }
    }
}, [drawingToolValues]);

useEffect(() => {
  if (useManualTpSl && drawingToolValues && drawingToolValues.drawingId) {
      console.log('Manual TP & SL set');
      setTimeout(() => {
          console.log('Setting entry price:', drawingToolValues.entryValue);
          console.log('Setting stop loss:', drawingToolValues.stopLossValue);
          console.log('Setting take profit:', drawingToolValues.takeProfitValue);
          console.log('Setting drawing ID:', drawingToolValues.drawingId);
          setEntryPrice(Number(drawingToolValues.entryValue));
          setCustomStopLoss(Number(drawingToolValues.stopLossValue));
          setCustomTakeProfit(Number(drawingToolValues.takeProfitValue));
          setStopLoss(Number(drawingToolValues.stopLossValue)); // Set stop loss from drawing tool
          setTakeProfit(Number(drawingToolValues.takeProfitValue)); // Set take profit from drawing tool
      }, 300); // Adjust delay as necessary
  }
}, [useManualTpSl, drawingToolValues]);
  
  const pairs = [
    { value: 'AUDCAD', label: 'AUDCAD' },
    { value: 'AUDCHF', label: 'AUDCHF' },
    { value: 'AUDJPY', label: 'AUDJPY' },
    { value: 'AUDNZD', label: 'AUDNZD' },
    { value: 'AUDUSD', label: 'AUDUSD' },
    { value: 'CADCHF', label: 'CADCHF' },
    { value: 'CADJPY', label: 'CADJPY' },
    { value: 'CHFJPY', label: 'CHFJPY' },
    { value: 'EURAUD', label: 'EURAUD' },
    { value: 'EURCAD', label: 'EURCAD' },
    { value: 'EURCHF', label: 'EURCHF' },
    { value: 'EURGBP', label: 'EURGBP' },
    { value: 'EURNZD', label: 'EURNZD' },
    { value: 'EURUSD', label: 'EURUSD' },
    { value: 'GBPAUD', label: 'GBPAUD' },
    { value: 'GBPCAD', label: 'GBPCAD' },
    { value: 'GBPCHF', label: 'GBPCHF' },
    { value: 'GBPJPY', label: 'GBPJPY' },
    { value: 'GBPNZD', label: 'GBPNZD' },
    { value: 'GBPUSD', label: 'GBPUSD' },
    { value: 'NZDCAD', label: 'NZDCAD' },
    { value: 'NZDCHF', label: 'NZDCHF' },
    { value: 'NZDJPY', label: 'NZDJPY' },
    { value: 'NZDUSD', label: 'NZDUSD' },
    { value: 'USDCAD', label: 'USDCAD' },
    { value: 'USDCHF', label: 'USDCHF' },
    { value: 'USDJPY', label: 'USDJPY' },
    { value: 'AUDGBP', label: 'AUDGBP' },
    { value: 'AUDHKD', label: 'AUDHKD' },
    { value: 'AUDSGD', label: 'AUDSGD' },
    { value: 'CHFHUF', label: 'CHFHUF' },
    { value: 'CHFPLN', label: 'CHFPLN' },
    { value: 'EURAED', label: 'EURAED' },
    { value: 'EURCZK', label: 'EURCZK' },
    { value: 'EURDKK', label: 'EURDKK' },
    { value: 'EURHKD', label: 'EURHKD' },
    { value: 'EURHUF', label: 'EURHUF' },
    { value: 'GBPCZK', label: 'GBPCZK' },
    { value: 'GBPDKK', label: 'GBPDKK' },
    { value: 'GBPTRY', label: 'GBPTRY' },
    { value: 'HKDJPY', label: 'HKDJPY' },
    { value: 'NZDSGD', label: 'NZDSGD' },
    { value: 'SGDJPY', label: 'SGDJPY' },
    { value: 'USDCNH', label: 'USDCNH' },
    { value: 'USDHKD', label: 'USDHKD' },
    { value: 'USDSGD', label: 'USDSGD' },
    { value: 'USDTRY', label: 'USDTRY' },
    { value: 'USDZAR', label: 'USDZAR' },
    { value: 'ZARJPY', label: 'ZARJPY' },
  ];
  const exchangeRatesCache = {};
  const fetchExchangeRates = async (symbol) => {
    try {
        // Check if the symbol includes USD
        if (symbol.includes('USD')) {
            console.log('Symbol includes USD, no need to fetch exchange rates.');
            return { [`${symbol}`]: 1 }; // Return a default exchange rate of 1
        }

        // Check if exchange rates are already cached
        if (exchangeRatesCache[symbol]) {
            console.log('Using cached exchange rates:', exchangeRatesCache[symbol]);
            return exchangeRatesCache[symbol];
        }

        // Call storePlaybackTime on the CustomDatafeed instance
        if (datafeed) {
            datafeed.storePlaybackTime();
        } else {
            console.error('datafeed is null');
            return null;
        }

        // Ensure currentPlaybackTime is updated after calling storePlaybackTime
        const updatedPlaybackTime = datafeed.currentPlaybackTime;
        console.log('Updated playback time:', updatedPlaybackTime);
        if (!updatedPlaybackTime) {
            console.error('currentPlaybackTime is null');
            return null;
        }

        const baseCurrency = symbol.slice(0, 3);
        const quoteCurrency = symbol.slice(3);
        const exchangeSymbol = quoteCurrency === 'USD' ? `${baseCurrency}USD` : `USD${quoteCurrency}`;

        const startDate = new Date(updatedPlaybackTime).toISOString().slice(0, 19).replace('T', ' ');
        const endDate = new Date(updatedPlaybackTime).toISOString().slice(0, 19).replace('T', ' ');
        const intervals = '1';

        // Check Firebase for existing data
        const docRef = doc(db, 'Users', userID, 'backtesting', sessionId, 'exchangeRates');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            const marketData = data[exchangeSymbol];
            if (marketData && marketData.length > 0) {
                const currentCandle = marketData[marketData.length - 1];
                exchangeRatesCache[symbol] = { [exchangeSymbol]: currentCandle.close }; // Cache the fetched exchange rates
                return exchangeRatesCache[symbol];
            }
        }

        // If data is not available in Firebase, fetch from API
        const marketData = await fetchMarketData(exchangeSymbol, '1min', startDate, endDate, '1min', 1);

        if (marketData.length > 0) {
            const currentCandle = marketData[marketData.length - 1];
            exchangeRatesCache[symbol] = { [exchangeSymbol]: currentCandle.close }; // Cache the fetched exchange rates

            return exchangeRatesCache[symbol];
        } else {
            console.error('No market data available for the current playback time.');
            return null;
        }
    } catch (error) {
        console.error('Error fetching exchange rate:', error);
        return null;
    }
  };
  
  const mapToUSD = (exchangeRates) => {
    console.log('mapToUSD -> exchangeRates:', exchangeRates); // Debug log

    const usdRates = {};

    for (const pair in exchangeRates) {
        const base = pair.slice(0, 3);
        const quote = pair.slice(3);
        console.log('mapToUSD -> base:', base); // Debug log
        console.log('mapToUSD -> quote:', quote); // Debug log

        if (quote === 'USD') {
            usdRates[base] = exchangeRates[pair];
        } else if (base === 'USD') {
            usdRates[quote] = 1 / exchangeRates[pair];
        }
    }

    console.log('mapToUSD -> usdRates:', usdRates); // Debug log
    return usdRates;
  };

  // Function declaration for getPipValue
  function getPipValue(symbol) {
    console.log('getPipValue -> symbol:', symbol); // Debug log
    let pipValue;
  
    // Handle special cases for certain symbols
    if (symbol.endsWith('JPY')) {
      pipValue = 0.01;
    } else if (['XAUUSD', 'XAGUSD', 'WTIUSD', 'BRENTUSD', 'COPPERUSD', 'SUGARUSD', 'NGASUSD'].includes(symbol)) {
      // Set pip values for commodities
      switch (symbol) {
        case 'XAUUSD': // Gold
          pipValue = 0.01;
          break;
        case 'XAGUSD': // Silver
          pipValue = 0.001;
          break;
        case 'WTIUSD':
        case 'BRENTUSD': // Oil
          pipValue = 0.01;
          break;
        case 'COPPERUSD': // Copper
          pipValue = 0.0001;
          break;
        case 'SUGARUSD': // Sugar
          pipValue = 0.0001;
          break;
        case 'NGASUSD': // Natural Gas
          pipValue = 0.001;
          break;
        default:
          pipValue = 0.0001; // Default for other commodities
      }
    } else {
      pipValue = 0.0001; // Default for currency pairs
    }
    console.log('getPipValue -> pipValue:', pipValue); // Debug log
    return pipValue;
  }

  const getPipValueInDollars = (symbol, lotSize) => {
    console.log('getPipValueInDollars -> symbol:', symbol); // Debug log
  
    let pipValueInDollars;
    if (symbol === 'XAUUSD') {
      pipValueInDollars = 1 * lotSize; // Gold pip value
    } else if (symbol === 'XAGUSD') {
      pipValueInDollars = 5 * lotSize; // Silver pip value
    } else if (['WTIUSD', 'BRENTUSD'].includes(symbol)) {
      pipValueInDollars = 10 * lotSize; // Oil pip value
    } else if (symbol === 'COPPERUSD') {
      pipValueInDollars = 25 * lotSize; // Copper pip value
    } else if (symbol === 'SUGARUSD') {
      pipValueInDollars = 11.2 * lotSize; // Sugar pip value
    } else if (symbol === 'NGASUSD') {
      pipValueInDollars = 10 * lotSize; // Natural Gas pip value
    } else if (symbol.endsWith('JPY')) {
      pipValueInDollars = (10 / 100) * lotSize; // Adjusted pip value for JPY pairs
    } else if (symbol.slice(3) === 'USD') {
      pipValueInDollars = 10 * lotSize; // Standard pip value when quote currency is USD
    } else {
      // For pairs where USD is the base currency or other crosses
      // Adjust according to live exchange rates if necessary
      pipValueInDollars = 10 * lotSize; // Default pip value
    }
    console.log('getPipValueInDollars -> pipValueInDollars:', pipValueInDollars); // Debug log
    return pipValueInDollars;
  };

  const fetchAndCacheExchangeRates = async (symbol) => {
    let exchangeRates = cachedExchangeRates;
    if (!exchangeRates) {
        exchangeRates = await fetchExchangeRates(symbol);
        setCachedExchangeRates(exchangeRates);
    }
    return exchangeRates;
};

const getContractSize = (symbol) => {
  if (symbol === 'XAUUSD') {
    return 100; // Gold contract size
  } else if (symbol === 'XAGUSD') {
    return 5000; // Silver contract size
  } else if (['WTIUSD', 'BRENTUSD'].includes(symbol)) {
    return 1000; // Oil contract size
  } else if (symbol === 'COPPERUSD') {
    return 25000; // Copper contract size
  } else if (symbol === 'SUGARUSD') {
    return 112000; // Sugar contract size
  } else if (symbol === 'NGASUSD') {
    return 10000; // Natural Gas contract size
  } else {
    return 100000; // Default for currency pairs
  }
};

const calculateStopLoss = (entryPrice, riskAmount, pipValue, amount, useLots, orderSide, exchangeRates, symbol) => {
  console.log('calculateStopLoss -> entryPrice:', entryPrice); // Debug log
  console.log('calculateStopLoss -> riskAmount:', riskAmount); // Debug log
  console.log('calculateStopLoss -> pipValue:', pipValue); // Debug log
  console.log('calculateStopLoss -> amount:', amount); // Debug log
  console.log('calculateStopLoss -> useLots:', useLots); // Debug log
  console.log('calculateStopLoss -> exchangeRates:', exchangeRates); // Debug log

  if (!exchangeRates) {
      console.error('exchangeRates is null');
      return null;
  }

  // Get pip value in dollars specific to the symbol
  const pipValueInDollars = getPipValueInDollars(symbol, amount);
  console.log('calculateStopLoss -> pipValueInDollars:', pipValueInDollars); // Debug log

  // Calculate the number of pips to risk
  const pipDifference = riskAmount / pipValueInDollars;
  console.log('calculateStopLoss -> pipDifference:', pipDifference); // Debug log

  // Calculate the stop loss price
  const stopLossPrice = orderSide === 'buy'
      ? entryPrice - (pipDifference * pipValue)
      : entryPrice + (pipDifference * pipValue);
  console.log('stopLossPrice:', stopLossPrice);

  return stopLossPrice;
};

const calculateTakeProfit = (entryPrice, riskAmount, rrTarget, pipValue, amount, useLots, orderSide, exchangeRates, symbol) => {
  console.log('calculateTakeProfit -> entryPrice:', entryPrice); // Debug log
  console.log('calculateTakeProfit -> riskAmount:', riskAmount); // Debug log
  console.log('calculateTakeProfit -> rrTarget:', rrTarget); // Debug log
  console.log('calculateTakeProfit -> pipValue:', pipValue); // Debug log
  console.log('calculateTakeProfit -> amount:', amount); // Debug log
  console.log('calculateTakeProfit -> useLots:', useLots); // Debug log
  console.log('calculateTakeProfit -> exchangeRates:', exchangeRates); // Debug log

  if (!exchangeRates) {
      console.error('exchangeRates is null');
      return null;
  }

  // Get pip value in dollars specific to the symbol
  const pipValueInDollars = getPipValueInDollars(symbol, amount);
  console.log('calculateTakeProfit -> pipValueInDollars:', pipValueInDollars); // Debug log

  // Calculate the number of pips for the reward
  const rewardAmount = riskAmount * rrTarget;
  const pipDifference = rewardAmount / pipValueInDollars;
  console.log('calculateTakeProfit -> pipDifference:', pipDifference); // Debug log

  // Calculate the take profit price
  const takeProfitPrice = orderSide === 'buy'
      ? entryPrice + (pipDifference * pipValue)
      : entryPrice - (pipDifference * pipValue);
  console.log('takeProfitPrice:', takeProfitPrice);

  return takeProfitPrice;
};

const calculateEstimatedLoss = async (entryPrice, calculatedSLValue, pipDollarValue, amount, useLots, symbol, exchangeRates) => {
  console.log('calculateEstimatedLoss -> symbol:', symbol);

  if (!exchangeRates) {
    console.error('Exchange rates are not provided.');
    return null;
  }

  const baseCurrency = symbol.slice(0, 3);
  const quoteCurrency = symbol.slice(3);
  const contractSize = getContractSize(symbol);

  const estimatedLossInQuoteCurrency = useLots
    ? (entryPrice - calculatedSLValue) * amount * contractSize
    : (entryPrice - calculatedSLValue) * (amount / entryPrice);

  const exchangeRateKey = quoteCurrency === 'USD' ? null : `USD${quoteCurrency}`;
  const exchangeRate = exchangeRateKey ? exchangeRates[exchangeRateKey] : 1;

  const adjustedEstimatedLoss = estimatedLossInQuoteCurrency / exchangeRate;
  console.log('calculateEstimatedLoss -> adjustedEstimatedLoss:', adjustedEstimatedLoss);

  return Math.abs(adjustedEstimatedLoss);
};

const calculateEstimatedProfit = (entryPrice, calculatedTPValue, pipDollarValue, amount, useLots, symbol, exchangeRates) => {
  if (!exchangeRates) {
    console.error('Exchange rates are not provided.');
    return null;
  }

  const baseCurrency = symbol.slice(0, 3);
  const quoteCurrency = symbol.slice(3);
  const contractSize = getContractSize(symbol);

  const estimatedProfitInQuoteCurrency = useLots
    ? (calculatedTPValue - entryPrice) * amount * contractSize
    : (calculatedTPValue - entryPrice) * (amount / entryPrice);

  const exchangeRateKey = quoteCurrency === 'USD' ? null : `USD${quoteCurrency}`;
  const exchangeRate = exchangeRateKey ? exchangeRates[exchangeRateKey] : 1;

  const adjustedEstimatedProfit = estimatedProfitInQuoteCurrency / exchangeRate;
  console.log('calculateEstimatedProfit -> adjustedEstimatedProfit:', adjustedEstimatedProfit);

  return Math.abs(adjustedEstimatedProfit);
};

const calculateOrderValues = async (entryPrice, riskPercent, rrTarget, amount, useLots, orderSide, symbol) => {
    const riskAmount = (accountBalance * riskPercent) / 100;
    const pipValue = getPipValue(symbol);
    const exchangeRates = await fetchAndCacheExchangeRates(symbol);

    const rawSLValue = calculateStopLoss(entryPrice, riskAmount, pipValue, amount, useLots, orderSide, exchangeRates, symbol);
    const rawTPValue = calculateTakeProfit(entryPrice, riskAmount, rrTarget, pipValue, amount, useLots, orderSide, exchangeRates, symbol);

    const calculatedSLValue = typeof rawSLValue === 'number' ? rawSLValue.toFixed(5) : rawSLValue;
    const calculatedTPValue = typeof rawTPValue === 'number' ? rawTPValue.toFixed(5) : rawTPValue;

    const estimatedLoss = await calculateEstimatedLoss(entryPrice, parseFloat(calculatedSLValue), pipValue, amount, useLots, symbol, exchangeRates);
    const estimatedProfit = calculateEstimatedProfit(entryPrice, parseFloat(calculatedTPValue), pipValue, amount, useLots, symbol, exchangeRates);

    return { calculatedSLValue, calculatedTPValue, estimatedProfit, estimatedLoss };
};

const handleRiskAndRewardUpdate = useCallback(async () => {
    let calculatedEntryPrice = entryPrice;
    console.log('Initial calculatedEntryPrice:', calculatedEntryPrice);

    const currentSymbol = symbol || symbol;

    if (orderType === 'market') {
        console.log('Order type is market.');
        if (!cachedCandlePrice) {
            console.log('No cached candle price, fetching current candle price...');
            // Fetch the current candle price and set it as the entry price
            const currentCandlePrice = await fetchCurrentCandlePrice(currentSymbol);
            calculatedEntryPrice = currentCandlePrice;
            console.log('Fetched current candle price:', currentCandlePrice);
        } else {
            calculatedEntryPrice = cachedCandlePrice;
            console.log('Using cached candle price:', cachedCandlePrice);
        }
    }

    if (!calculatedEntryPrice) {
        console.error('Failed to fetch current candle price for market order.');
        return;
    }

    const exchangeRatesToUse = await fetchAndCacheExchangeRates(currentSymbol);
    console.log('exchangeRatesToUse:', exchangeRatesToUse);

    let calculatedSLValue, calculatedTPValue, estimatedProfit, estimatedLoss;

    if (useManualTpSl) {
        // If manual TP & SL is enabled, do not perform auto calculations
        const pipValue = getPipValue(symbol, exchangeRatesToUse);
        const riskAmount = await reverseCalculateRiskAmount(calculatedEntryPrice, customStopLoss, pipValue, amount, useLots, accountBalance, exchangeRatesToUse, symbol);
        const rrTarget = reverseCalculateRrTarget(calculatedEntryPrice, customStopLoss, customTakeProfit, pipValue, amount, useLots);
        // Calculate estimated loss and profit
        estimatedLoss = await calculateEstimatedLoss(calculatedEntryPrice, customStopLoss, pipValue, amount, useLots, symbol, exchangeRatesToUse);
        estimatedProfit = await calculateEstimatedProfit(calculatedEntryPrice, customTakeProfit, pipValue, amount, useLots, symbol, exchangeRatesToUse);

        setCustomRiskAmount(riskAmount);
        setCustomRrTarget(rrTarget);
        setEstimatedLoss(estimatedLoss);
        setEstimatedProfit(estimatedProfit);
    } else {
        ({ calculatedSLValue, calculatedTPValue, estimatedProfit, estimatedLoss } = await calculateOrderValues(
            calculatedEntryPrice, riskPercent, rrTarget, amount, useLots, orderSide, currentSymbol
        ));

        setCalculatedSL(calculatedSLValue);
        setCalculatedTP(calculatedTPValue);
        setEstimatedProfit(estimatedProfit);
        setEstimatedLoss(estimatedLoss);
        setStopLoss(calculatedSLValue); // Automatically render stop loss
        setTakeProfit(calculatedTPValue); // Automatically render take profit
    }
}, [amount, riskPercent, rrTarget, useLots, orderSide, symbol, exchangeRates, currentPlaybackTime, orderType, entryPrice, cachedCandlePrice, cachedExchangeRates, customStopLoss, customTakeProfit, useManualTpSl]);

useEffect(() => {
    handleRiskAndRewardUpdate();
}, [riskPercent, amount, entryPrice, rrTarget, useLots, orderSide, orderType, stopLoss, takeProfit, useManualTpSl]);

const updateCachedCandlePrice = async () => {
    if (isModalOpen && !isPlaybackActive) {
        console.log('Updating cached candle price...');
        const currentCandlePrice = await fetchCurrentCandlePrice(symbol, currentPlaybackTime);
        console.log('Fetched current candle price for cache:', currentCandlePrice);
        setCachedCandlePrice(currentCandlePrice);
    }
};

const calculateManualRiskAndReward = (entryPrice, customStopLoss, customTakeProfit, amount, accountBalance, symbol) => {
  console.log('calculateManualRiskAndReward -> entryPrice:', entryPrice);
  console.log('calculateManualRiskAndReward -> customStopLoss:', customStopLoss);
  console.log('calculateManualRiskAndReward -> customTakeProfit:', customTakeProfit);
  console.log('calculateManualRiskAndReward -> amount:', amount);
  console.log('calculateManualRiskAndReward -> accountBalance:', accountBalance);
  console.log('calculateManualRiskAndReward -> symbol:', symbol);

  // Get contract size for the symbol
  const contractSize = getContractSize(symbol);

  // Calculate pip difference for risk (SL)
  const riskPipDifference = Math.abs(entryPrice - customStopLoss);
  console.log('calculateManualRiskAndReward -> riskPipDifference:', riskPipDifference);

  // Calculate risk amount
  const riskAmount = riskPipDifference * amount * contractSize;
  console.log('calculateManualRiskAndReward -> riskAmount:', riskAmount);

  // Calculate risk percentage
  const riskPercentage = (riskAmount / accountBalance) * 100;
  console.log('calculateManualRiskAndReward -> riskPercentage:', riskPercentage);

  // Calculate pip difference for reward (TP)
  const rewardPipDifference = Math.abs(customTakeProfit - entryPrice);
  console.log('calculateManualRiskAndReward -> rewardPipDifference:', rewardPipDifference);

  // Calculate estimated profit
  const estimatedProfit = rewardPipDifference * amount * contractSize;
  console.log('calculateManualRiskAndReward -> estimatedProfit:', estimatedProfit);

  // Calculate risk-reward ratio
  const rrTarget = estimatedProfit / riskAmount;
  console.log('calculateManualRiskAndReward -> rrTarget:', rrTarget);

  return {
    riskAmount: riskAmount.toFixed(2),
    riskPercentage: riskPercentage.toFixed(2),
    estimatedLoss: riskAmount.toFixed(2),
    estimatedProfit: estimatedProfit.toFixed(2),
    rrTarget: rrTarget.toFixed(2)
  };
};

const reverseCalculateRiskAmount = async (entryPrice, customStopLoss, pipValue, amount, useLots, accountBalance, exchangeRates, symbol) => {
  console.log('reverseCalculateRiskAmount -> entryPrice:', entryPrice);
  console.log('reverseCalculateRiskAmount -> customStopLoss:', customStopLoss);
  console.log('reverseCalculateRiskAmount -> pipValue:', pipValue);
  console.log('reverseCalculateRiskAmount -> amount:', amount);
  console.log('reverseCalculateRiskAmount -> useLots:', useLots);
  console.log('reverseCalculateRiskAmount -> accountBalance:', accountBalance);
  console.log('reverseCalculateRiskAmount -> exchangeRates:', exchangeRates);
  console.log('reverseCalculateRiskAmount -> symbol:', symbol);

  if (!symbol) {
    console.error('Symbol is undefined.');
    return null;
  }

  // Get contract size for the symbol
  const contractSize = getContractSize(symbol);

  // Calculate pip difference
  const pipDifference = Math.abs(entryPrice - customStopLoss) / pipValue;
  console.log('reverseCalculateRiskAmount -> pipDifference:', pipDifference);

  // Calculate risk per pip
  const riskPerPip = useLots ? pipValue * amount * contractSize : pipValue * (amount / entryPrice);
  console.log('reverseCalculateRiskAmount -> riskPerPip:', riskPerPip);

  // Calculate risk amount
  const riskAmount = pipDifference * riskPerPip;
  console.log('reverseCalculateRiskAmount -> riskAmount:', riskAmount);

  // Adjust risk amount based on exchange rate
  const exchangeRateKey = null; // For XAUUSD, the exchange rate is 1
  const exchangeRate = 1;       // Since the quote currency is USD

  // Adjusted risk amount remains the same
  const adjustedRiskAmount = riskAmount / exchangeRate;
  console.log('reverseCalculateRiskAmount -> adjustedRiskAmount:', adjustedRiskAmount);

  // Calculate risk percentage based on account balance
  const riskPercent = (adjustedRiskAmount / accountBalance) * 100;
  console.log('reverseCalculateRiskAmount -> riskPercent:', riskPercent);

  return riskPercent.toFixed(2);
};

const reverseCalculateRrTarget = (entryPrice, customStopLoss, customTakeProfit, pipValue, amount, useLots) => {
    const riskPipDifference = Math.abs(entryPrice - customStopLoss) / pipValue;
    const rewardPipDifference = Math.abs(customTakeProfit - entryPrice) / pipValue;
    const rrTarget = rewardPipDifference / riskPipDifference;
    return rrTarget.toFixed(2);
};

const getAccountBalance = async (userID, currentSessionId) => {
    const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
        console.error('Session document does not exist.');
        return 0;
    }

    const sessionData = sessionDoc.data();
    return parseFloat(sessionData.accountBalance) || 0;
};
  const getTotalProfitLoss = async (userID, currentSessionId) => {
    const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
    const tradesCollectionRef = collection(sessionRef, 'trades');
    const tradesSnapshot = await getDocs(tradesCollectionRef);
  
    let totalProfitLoss = 0;
    tradesSnapshot.forEach(doc => {
      const tradeData = doc.data();
      if (tradeData.profitLoss) {
        totalProfitLoss += tradeData.profitLoss;
      }
    });
  
    return totalProfitLoss;
  };



  const displayUpdatedAccountBalance = async (userID, currentSessionId) => {
    const updatedAccountBalance = await calculateUpdatedAccountBalance(userID, currentSessionId);
    console.log(`Updated Account Balance: $${updatedAccountBalance.toFixed(2)}`);
  };

    // Function to update the modal with order details
    const updateOrderModal = (order, estimatedLoss, estimatedProfit) => {
      if (typeof estimatedLoss !== 'number' || typeof estimatedProfit !== 'number') {
        console.error('Estimated loss or profit is not a number');
        return;
      }
      setModalContent({
        title: 'Order Details',
        order,
        estimatedLoss,
        estimatedProfit,
        message: `Estimated Loss: $${estimatedLoss.toFixed(2)}, Estimated Profit: $${estimatedProfit.toFixed(2)}`
      });
    
      // Show the modal
      setIsOrderModalOpen(false);  // Open modal with details
      setIsLoading(true);
    };
    
    const placeOrder = useCallback(async (type, price, size, stopLoss, takeProfit, risk, reward) => {
      if (!currentSessionId) {
        console.error('No active session ID found.');
        setTradeSnackbarMessage('No active session ID found.');
        setTradeSnackbarColor('error');
        setTradeSnackbarOpen(true);
        return;
      }
    
      // Clear errors
      setOrderSideError(false);
      setOrderTypeError(false);
      setAmountError(false);
      setEntryPriceError(false);
    
      let isValid = true;
      if (!orderSide) {
        setOrderSideError(true);
        isValid = false;
      }
      if (!orderType) {
        setOrderTypeError(true);
        isValid = false;
      }
      if (!amount) {
        setAmountError(true);
        isValid = false;
      }
      if (orderType === 'limit' && !entryPrice) {
        setEntryPriceError(true);
        isValid = false;
      }
    
      if (!isValid) {
        setShowSnackbar(true);
        setSnackbarMessage('Please fill out all required fields.');
        setSnackbarColor('error');
        return;
      }
    
      let calculatedEntryPrice;
      let currentCandlePrice = cachedCandlePrice;
    
      if (orderType === 'limit') {
        calculatedEntryPrice = parseFloat(entryPrice);
      } else if (orderType === 'market') {
        if (!currentCandlePrice) {
          currentCandlePrice = await fetchCurrentCandlePrice(symbol, currentPlaybackTime);
          if (!currentCandlePrice) {
            console.error('Failed to fetch current candle price for market order.');
            setTradeSnackbarMessage('Failed to fetch current market price.');
            setTradeSnackbarColor('error');
            setTradeSnackbarOpen(true);
            return;
          }
          setCachedCandlePrice(currentCandlePrice);
        }
        calculatedEntryPrice = currentCandlePrice;
      }
    
      let orderStatus = 'Open';
    
      console.log('Final calculatedEntryPrice:', calculatedEntryPrice);
      console.log('Current candle price:', currentCandlePrice);
      console.log('User-defined entry price:', entryPrice);
    
      // Fetch and cache exchange rates
      let exchangeRates = cachedExchangeRates;
      if (!exchangeRates) {
        exchangeRates = await fetchExchangeRates(symbol);
        setCachedExchangeRates(exchangeRates);
      }
    
      // Check for limit orders
      if (orderType === 'limit') {
        if (orderSide === 'sell') {
          orderStatus = currentCandlePrice !== calculatedEntryPrice ? 'Pending' : orderStatus;
          if (orderStatus === 'Pending') {
            console.log('Order status set to Pending: Sell order condition met.');
          }
        } else if (orderSide === 'buy') {
          orderStatus = currentCandlePrice !== calculatedEntryPrice ? 'Pending' : orderStatus;
          if (orderStatus === 'Pending') {
            console.log('Order status set to Pending: Buy order condition met.');
          }
        }
      }
    
      console.log('Final order status:', orderStatus);
    
      // Determine the effective risk percent to use
      const effectiveRiskPercent = useManualTpSl && customRiskAmount !== null ? customRiskAmount : riskPercent;
      console.log('Effective risk percent:', effectiveRiskPercent, { useManualTpSl, customRiskAmount, riskPercent });
    
      const { calculatedSLValue, calculatedTPValue, estimatedProfit, estimatedLoss } = await calculateOrderValues(
        calculatedEntryPrice,
        effectiveRiskPercent,
        rrTarget,
        amount,
        useLots,
        orderSide,
        symbol,
        exchangeRates
      );
    
      // Set SL and TP only if useStopLossTakeProfit is true
      const finalStopLoss = useStopLossTakeProfit
        ? (useManualTpSl && customStopLoss !== null ? parseFloat(customStopLoss) : parseFloat(calculatedSLValue))
        : null;
      const finalTakeProfit = useStopLossTakeProfit
        ? (useManualTpSl && customTakeProfit !== null ? parseFloat(customTakeProfit) : parseFloat(calculatedTPValue))
        : null;
    
      setCalculatedSL(finalStopLoss);
      setCalculatedTP(finalTakeProfit);
    
      console.log('Order status determined as:', orderStatus);
    
      // Fetch the current size of the trades collection to determine the next trade number
      const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
      const tradesCollectionRef = collection(sessionRef, 'trades');
      const tradesSnapshot = await getDocs(tradesCollectionRef);
      const tradeNumber = tradesSnapshot.size + 1;
    
      const newOrder = {
        type,
        price: calculatedEntryPrice,
        size,
        side: orderSide === 'buy' ? 'Buy' : 'Sell',
        amount: useLots ? `${amount} lots` : `$${amount}`,
        stopLoss: finalStopLoss,
        takeProfit: finalTakeProfit,
        orderType,
        status: orderStatus,
        riskPercent: Number(effectiveRiskPercent),
        rrTarget,
        currentPlaybackTime: datafeed?.currentPlaybackTime || currentPlaybackTime,
        cachedExchangeRates: exchangeRates,
        estimatedLoss,
        estimatedProfit,
        exitPrice: null,
        profitLoss: 0,
        exitDate: null,
        tradeNumber,
        symbol,
        id: `Trade ${tradeNumber}`,
        useStopLossTakeProfit
      };
    
      // Save the trade to Firestore
      try {
        console.log('Saving trade with riskPercent:', newOrder.riskPercent);
        const tradeRef = doc(tradesCollectionRef, `Trade ${tradeNumber}`);
        await setDoc(tradeRef, newOrder);
    
        await updateDoc(sessionRef, { currentPlaybackTime: datafeed?.currentPlaybackTime || currentPlaybackTime });
    
        setOrders(prevOrders => [...prevOrders, newOrder]);

        updateOrderModal(newOrder, estimatedLoss, estimatedProfit);
    
        setTradeSnackbarMessage(`Trade #${tradeNumber} placed successfully`);
        setTradeSnackbarColor('success');
        setTradeSnackbarOpen(true);

        handleIsOrderModalOpen(false);
      } catch (error) {
        console.error('Error saving trade:', error);
        setTradeSnackbarMessage('Failed to place order');
        setTradeSnackbarColor('error');
        setTradeSnackbarOpen(true);
      }
    }, [
      orderType,
      entryPrice,
      amount,
      riskPercent,
      rrTarget,
      customStopLoss,
      customTakeProfit,
      orderSide,
      useLots,
      useManualTpSl,
      customRiskAmount,
      symbol,
      currentSessionId,
      userID,
      datafeed,
      cachedCandlePrice,
      cachedExchangeRates,
      currentPlaybackTime,
      updateOrderModal,
      setModalContent,
      setIsOrderModalOpen,
      setIsLoading,
      useStopLossTakeProfit
    ]);

  // Function to fetch the current candle price
  const fetchCurrentCandlePrice = async (symbol, interval) => {
    console.log('Fetching current candle price for', symbol, 'at interval', interval);
    
    try {
      // First try to get price from datafeed if available
      if (datafeed) {
        const currentTime = datafeed.getCurrentPlaybackTime();
        const playbackData = datafeed.playbackBarsData;
        
        if (playbackData && playbackData.length > 0) {
          // Find the most recent candle at or before the current playback time
          const currentCandle = playbackData
            .filter(bar => bar.time <= currentTime)
            .sort((a, b) => b.time - a.time)[0];
          
          if (currentCandle) {
            console.log('Found current candle from datafeed:', currentCandle);
            return currentCandle.close;
          }
        }
      }
      
      // If no price is found from datafeed, check if there's a cached candle price
      if (cachedCandlePrice !== null) {
        console.log('Using cached candle price:', cachedCandlePrice);
        return cachedCandlePrice;
      }
      
      // If we still don't have a price, try getting the last item from data
      if (data.length > 0) {
        const lastCandle = data[data.length - 1];
        console.log('Using last candle from data array:', lastCandle);
        return lastCandle.close;
      }
      
      throw new Error('No price data available');
    } catch (error) {
      console.error('Failed to fetch current candle price for market order.', error);
      // Show a user-friendly error message

      return null;
    }
  };

  useEffect(() => {
    if (!isPlaying || !datafeed) return;
  
    const monitorCandleData = async (userID, currentSessionId, sessionId) => {
      if (!datafeed || datafeed.currentPlaybackTime === undefined) {
        console.error("Playback time is undefined!");
        return;
      }
    
      const currentCandle = await fetchCurrentCandle(symbol, currentPlaybackTime);
      console.log('currentPlaybackTime:', currentPlaybackTime);
      const currentPrice = currentCandle.close;
      console.log('Current Price:', currentPrice);
      const highPrice = currentCandle.high;
      const lowPrice = currentCandle.low;
      const playbackTime = currentCandle.time;
        
      setCurrentPrice(currentPrice); 
      // Add this section to check trailing stops
      console.log("Checking trailing stops for open trades");
      tradeHistory.forEach(trade => {
        if (trade.status === 'Open' && trade.trailingStopLoss) {
          console.log("Found trade with trailing stop:", trade.tradeNumber);
          applyTrailingStopLoss(trade, currentPrice, playbackTime);
        }
      });  
      setTradeHistory(prevTradeHistory => {
        const updatedTradeHistory = prevTradeHistory.map(trade => {
          if (trade.status === 'closed') return trade;
  
          if (trade.orderType === 'limit' && trade.status === 'Pending') {
             checkLimitOrderHit(currentPrice, trade, highPrice, lowPrice, userID, sessionId, currentSessionId, playbackTime);
           }
  
          if (trade.status === 'Open') {
             checkTakeProfitStopLoss(currentPrice, trade, highPrice, lowPrice, userID, sessionId, currentSessionId, playbackTime);
             const unrealizedProfit = calculateUnrealizedProfit(currentPrice, trade);
             console.log(`Unrealized Profit for Trade ${trade.tradeNumber}:`, unrealizedProfit);
             return { ...trade, unrealizedProfit };
          }
          return trade;
        });

        return updatedTradeHistory;
      });
    };  
    
    const intervalId = setInterval(() => monitorCandleData(userID, currentSessionId, sessionId), 1000);
    
    return () => clearInterval(intervalId);
  }, [isPlaying, datafeed, currentPlaybackTime, symbol, userID, currentSessionId, sessionId]);

  const editTrade = (tradeId, updatedTrade) => {
    setTradeHistory(prevTradeHistory => {
      return prevTradeHistory.map(trade => {
        if (trade.id === tradeId && trade.status === 'open') {
          return { ...trade, ...updatedTrade };
        }
        return trade;
      });
    });
  };

  const closeTrade = async (trade, currentPlaybackTime, contractSize) => {
    try {
      const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
      
      if (datafeed) {
        datafeed.storePlaybackTime();
      } else {
        console.warn('Datafeed not available, unable to store playback time');
      }
      
      const currentPrice = await fetchCurrentCandlePrice(symbol, interval);
      const profitLoss = await calculateProfitLoss(
        currentPrice, 
        trade.size, 
        trade.side, 
        userID, 
        currentSessionId, 
        trade.tradeNumber, 
        getContractSize(trade.symbol || symbol)
      );
      const tradeRef = doc(sessionRef, 'trades', `Trade ${trade.tradeNumber}`);
  
      let exitDate;
      try {
        exitDate = new Date(currentPlaybackTime).toISOString();
      } catch (error) {
        console.error('Invalid currentPlaybackTime:', currentPlaybackTime);
        exitDate = new Date().toISOString();
      }
  
      await updateDoc(tradeRef, { 
        status: 'Closed', 
        exitPrice: currentPrice, 
        profitLoss, 
        exitDate 
      });
  
      setTradeHistory(prev => prev.map(t => 
        t.tradeNumber === trade.tradeNumber ? 
        { ...t, status: 'Closed', exitPrice: currentPrice, profitLoss, exitDate } : 
        t
      ));
  
      // Remove account balance update here; useEffect will handle it
      await updateDoc(sessionRef, { 
        currentPlaybackTime: datafeed?.getCurrentPlaybackTime() || currentPlaybackTime,
        playbackTimes: datafeed?.playbackTimes || []
      });
  
      console.log('Trade closed and playback time updated');
    } catch (error) {
      console.error('Error closing trade:', error);
      setTradeSnackbarMessage('Error closing trade: ' + error.message);
      setTradeSnackbarColor('error');
      setTradeSnackbarOpen(true);
    }
  };
  const cancelTrade = async (trade) => {
    const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
    const tradeRef = doc(sessionRef, 'trades', `Trade ${trade.tradeNumber}`);
    await updateDoc(tradeRef, { status: 'Canceled' });
    setTradeHistory(prev => prev.map(t => t.tradeNumber === trade.tradeNumber ? { ...t, status: 'Canceled' } : t));
  };

  const calculateRunningPL = () => {
    return tradeHistory.reduce((acc, trade) => {
      if (trade.status === 'closed') {
        return acc + trade.profitLoss;
      }
      return acc;
    }, 0);
  };
  
  // Function to calculate total PnL
  const calculateTotalPnL = async (userID, currentSessionId) => {
    const totalProfitLoss = await getTotalProfitLoss(userID, currentSessionId);
    return totalProfitLoss;
  };
  
  useEffect(() => {
    const calculateRealizedPnL = async () => {
      if (!userID || !currentSessionId) {
        console.error('userID or currentSessionId is undefined.');
        return;
      }
  
      const accountBalance = await getAccountBalance(userID, currentSessionId);
      const totalProfitLoss = await getTotalProfitLoss(userID, currentSessionId);
      setRealizedPnL(totalProfitLoss);
    };
  
    calculateRealizedPnL();
  }, [tradeHistory, userID, currentSessionId]);

  // Updated calculateSessionTotalPnL to return P&L without modifying state
  const calculateSessionTotalPnL = async (userID, sessionId) => {
    try {
      const tradesRef = collection(db, 'Users', userID, 'backtesting', sessionId, 'trades');
      const tradesSnapshot = await getDocs(tradesRef);
      const trades = tradesSnapshot.docs.map(doc => doc.data());
      const totalPnL = trades
        .filter(trade => trade.status === 'Closed')
        .reduce((sum, trade) => sum + (trade.profitLoss || 0), 0);
      return totalPnL;
    } catch (error) {
      console.error('Error calculating session total PnL:', error);
      return 0;
    }
  };

  const calculateTotalUnrealizedPnL = (trades) => {
    return trades.reduce((total, trade) => {
      if (trade.status === 'Open' && typeof trade.unrealizedProfit === 'number') {
        return total + trade.unrealizedProfit;
      }
      return total;
    }, 0);
  };
  useEffect(() => {
    const totalUnrealizedPnL = calculateTotalUnrealizedPnL(tradeHistory);
    setTotalUnrealizedPnL(totalUnrealizedPnL);
  }, [tradeHistory]);

// Inside your component
const previousTradeHistoryRef = useRef([]);

useEffect(() => {
  const previousTradeHistory = previousTradeHistoryRef.current;

  // On initial load, store the current trade history and skip notifications
  if (previousTradeHistory.length === 0) {
    previousTradeHistoryRef.current = [...tradeHistory];
    return;
  }

  let snackbarMessages = [];
  let color = ''; // Default color

  tradeHistory.forEach((trade) => {
    const previousTrade = previousTradeHistory.find(
      (t) => t.tradeNumber === trade.tradeNumber
    );

    // Proceed only if the trade status has changed
    if (previousTrade && previousTrade.status !== trade.status) {
      let message = `Trade ${trade.tradeNumber} status changed to ${trade.status}`;

      if (trade.status === 'Closed') {
        const profitLoss = parseFloat(trade.profitLoss).toFixed(2);
        message += ` with a ${
          profitLoss >= 0 ? 'Profit' : 'Loss'
        } of $${profitLoss}`;
        color = profitLoss >= 0 ? 'rgba(100, 200, 100, 0.7)' : 'rgba(200, 100, 100, 0.7)';
      }

      snackbarMessages.push(message);
    }

    // If it's a new trade, notify about its status
    if (!previousTrade) {
      if (trade.status !== 'Closed') {
        const message = `New order placed with status ${trade.status}`;
        snackbarMessages.push(message);
      }
    }
  });

  if (snackbarMessages.length > 0) {
    setSnackbarMessage(snackbarMessages.join('\n'));
    setSnackbarColor(color);
    setShowSnackbar(true);
  }

  // Update the ref with the current trade history for the next comparison
  previousTradeHistoryRef.current = [...tradeHistory];
}, [tradeHistory]);

  const handleEditTradeOpen = (trade) => {
    console.log('Opening edit modal for trade:', trade); // Debugging log
    setEditTradeData({
      stopLoss: trade.stopLoss,
      takeProfit: trade.takeProfit,
      price: trade.status === 'Pending' ? trade.price : '',
      trailingStopLoss: trade.trailingStopLoss || false,
      trailingStopLossType: trade.trailingStopLossType || 'percentage',
      trailingStopLossValue: trade.trailingStopLossValue || '', // Default to 1%
      partialTakeProfit: trade.partialTakeProfit || false,
      partialTakeProfitPercent: trade.partialTakeProfitPercent || 0,
    });
    setSelectedTrade(trade); // Set the selected trade
    console.log('EditTradeData set:', {
      stopLoss: trade.stopLoss,
      takeProfit: trade.takeProfit,
      price: trade.status === 'Pending' ? trade.price : '',
      trailingStopLoss: trade.trailingStopLoss || false,
      trailingStopLossType: trade.trailingStopLossType || 'percentage',
      trailingStopLossValue: trade.trailingStopLossValue || '', // Default to 1%
      partialTakeProfit: trade.partialTakeProfit || false,
      partialTakeProfitPercent: trade.partialTakeProfitPercent || 0,
    });
    setIsEditTradeModalOpen(true);
    console.log('Edit modal should now open with selectedTrade:', trade);
  };
  
  const handleEditTradeChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log('Current state:', editTradeData);
    console.log(`Changing ${name} to ${type === 'checkbox' ? checked : value}`);
    
    // Force default value for percentage type
    if (name === 'trailingStopLossType' && value === 'percentage') {
      setEditTradeData(prev => ({
        ...prev,
        [name]: value,
        trailingStopLossValue: prev.trailingStopLossValue || ''
      }));
      return;
    }
  
    setEditTradeData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
  
  const handleEditTradeSubmit = async () => {
    if (!selectedTrade) {
      console.error('No trade selected for editing.');
      return;
    }
  
    try {
      const { stopLoss, takeProfit, price, trailingStopLoss, trailingStopLossType, trailingStopLossValue, trailingStopLossPercent, partialTakeProfit, partialTakeProfitPercent } = editTradeData;
      
    // First validate partial take profit settings if enabled
    if (partialTakeProfit && (partialTakeProfitPercent <= 0 || partialTakeProfitPercent > 100)) {
      setTradeSnackbarMessage('Invalid partial take profit percentage');
      setTradeSnackbarColor('rgba(200, 100, 100, 0.7)'); // Red for error
      setTradeSnackbarOpen(true);
      return;
    }
  
      // Prepare base updated data
      const updatedData = {
        stopLoss: parseFloat(stopLoss),
        takeProfit: parseFloat(takeProfit),
        partialTakeProfit,
        partialTakeProfitPercent: parseFloat(partialTakeProfitPercent || 0)
      };
  
      // Handle trailing stop loss fields
      if (trailingStopLoss) {
        updatedData.trailingStopLoss = true;
        updatedData.trailingStopLossType = trailingStopLossType || 'percentage';
        updatedData.trailingStopLossValue = parseFloat(trailingStopLossValue || 0);
      } else {
        updatedData.trailingStopLoss = false;
        updatedData.trailingStopLossType = 'percentage'; // Default value instead of undefined
        updatedData.trailingStopLossValue = 0;
      }
      // Check if stop loss is set to break even
      if (parseFloat(stopLoss) === selectedTrade.price) {
        setTradeSnackbarMessage('Stop Loss moved to Break Even - Trade is now risk free');
        setTradeSnackbarColor('rgba(255, 165, 0, 0.7)'); // Orange color for break-even
        setTradeSnackbarOpen(true);
        updatedData.isBreakEven = true; // Add flag for break-even status
      }  
      if (selectedTrade.status === 'Pending') {
        updatedData.price = parseFloat(price);
      }
  
      // Update the trade first
      const tradeRef = doc(db, 'Users', userID, 'backtesting', currentSessionId, 'trades', `Trade ${selectedTrade.tradeNumber}`);
      await updateDoc(tradeRef, updatedData);
  
      // Then try to apply partial take profit if enabled
      if (partialTakeProfit) {
        const currentPrice = await fetchCurrentCandlePrice(symbol, currentPlaybackTime);
        if (!currentPrice) {
          throw new Error('Could not fetch current price');
        }
        const mergedTrade = { ...selectedTrade, ...updatedData };
        const success = await applyPartialTakeProfit(mergedTrade, currentPrice);
        if (!success) {
          throw new Error('Failed to apply partial take profit');
        }
      } else {
        // Just update local state if no partial take profit
        setTradeHistory(prev =>
          prev.map(trade =>
            trade.tradeNumber === selectedTrade.tradeNumber
              ? { ...trade, ...updatedData }
              : trade
          )
        );
      }
  
      handleEditTradeClose();
  
    } catch (error) {
      console.error('Error updating trade:', error);
      setTradeSnackbarMessage('Failed to update trade: ' + error.message);
      setTradeSnackbarColor('rgba(200, 100, 100, 0.7)'); // Red for error
      setTradeSnackbarOpen(true);
    }
  };
  
  const handleEditTradeClose = () => {
    console.log('Closing edit modal for trade:', selectedTrade); // Debugging log
    setIsEditTradeModalOpen(false);
    setSelectedTrade(null); // Reset selected trade
    setIsEditAction(false); // Reset action state after modal closes
  };
  
  // Use useEffect to ensure modal opens only after `selectedTrade` is set
  useEffect(() => {
    if (isEditAction && selectedTrade) {
      setEditTradeData({
        stopLoss: selectedTrade.stopLoss,
        takeProfit: selectedTrade.takeProfit,
        price: selectedTrade.status === 'Pending' ? selectedTrade.price : '',
        trailingStopLoss: selectedTrade.trailingStopLoss || false,
        trailingStopLossPercent: selectedTrade.trailingStopLossPercent || 0,
        partialTakeProfit: selectedTrade.partialTakeProfit || false,
        partialTakeProfitPercent: selectedTrade.partialTakeProfitPercent || 0,
      });
      setIsEditTradeModalOpen(true);
      console.log('Edit modal should now open with selectedTrade:', selectedTrade);
    }
  }, [isEditAction, selectedTrade]); // Dependency on selectedTrade ensures modal opens correctly

// Update the price monitoring useEffect to include proper dependencies
useEffect(() => {
  if (!isPlaying) {
    console.log('Playback is paused');
    return;
  }

  if (!cachedCandlePrice) {
    console.log('No candle price available');
    return;
  }

  console.log('Price update detected:', {
    isPlaying,
    cachedCandlePrice,
    currentPlaybackTime,
    hasOpenTrades: tradeHistory.some(t => t.status === 'Open' && t.trailingStopLoss)
  });

  tradeHistory.forEach(trade => {
    if (trade.status === 'Open' && trade.trailingStopLoss) {
      console.log('Found trade with trailing stop:', {
        tradeNumber: trade.tradeNumber,
        currentPrice: cachedCandlePrice,
        trailingEnabled: trade.trailingStopLoss,
        trailingType: trade.trailingStopLossType,
        trailingValue: trade.trailingStopLossValue
      });
      
      applyTrailingStopLoss(trade, cachedCandlePrice, currentPlaybackTime);
    }
  });
}, [cachedCandlePrice, isPlaying, currentPlaybackTime, tradeHistory]);
// Add the new monitoring effect here
useEffect(() => {
  console.log('Monitoring price changes:', {
    isPlaying,
    cachedCandlePrice,
    hasOpenTrades: tradeHistory.some(t => t.status === 'Open' && t.trailingStopLoss)
  });
}, [cachedCandlePrice, isPlaying, tradeHistory]);
  const applyTrailingStopLoss = (trade, currentPrice, playbackTime) => {
    if (!trade || !trade.trailingStopLoss || !currentPrice) {
      console.log('Skipping trailing stop - missing params:', { trade, currentPrice });
      return;
    }
  
    const symbol = trade.symbol || 'EURUSD';
    
    // Calculate trail amount based on type
    let trailAmount;
    switch(trade.trailingStopLossType) {
      case 'pips':
        // 1 pip = 0.0001 for EURUSD, 0.01 for JPY pairs
        const pipValue = symbol.includes('JPY') ? 0.01 : 0.0001;
        trailAmount = trade.trailingStopLossValue * pipValue;
        break;
        
      case 'percentage':
        // Convert percentage to decimal and multiply by current price
        trailAmount = (trade.trailingStopLossValue / 100) * currentPrice;
        break;
        
      case 'price':
        // Use absolute price value directly
        trailAmount = parseFloat(trade.trailingStopLossValue);
        break;
        
      default:
        console.error('Invalid trailing stop type:', trade.trailingStopLossType);
        return;
    }
  
    console.log('Trailing stop calculation:', {
      type: trade.trailingStopLossType,
      value: trade.trailingStopLossValue,
      calculatedTrailAmount: trailAmount,
      currentPrice,
      currentStop: trade.stopLoss
    });
  
    if (trade.side === 'Buy') {
      const potentialNewStop = currentPrice - trailAmount;
      if (potentialNewStop > trade.stopLoss) {
        console.log('Updating buy trailing stop:', {
          from: trade.stopLoss,
          to: potentialNewStop,
          trailAmount
        });
        updateTrailingStop(trade, potentialNewStop, currentPrice, playbackTime);
      }
    } else {
      const potentialNewStop = currentPrice + trailAmount;
      if (potentialNewStop < trade.stopLoss) {
        console.log('Updating sell trailing stop:', {
          from: trade.stopLoss,
          to: potentialNewStop,
          trailAmount
        });
        updateTrailingStop(trade, potentialNewStop, currentPrice, playbackTime);
      }
    }
  };

// Update the updateTrailingStop function
// Update the updateTrailingStop function
const updateTrailingStop = async (trade, newStopLoss, currentPrice, playbackTime) => {
  try {
    console.log(`Updating trailing stop for trade ${trade.tradeNumber}`);
    console.log('Old stop:', trade.stopLoss);
    console.log('New stop:', newStopLoss);

    const tradeRef = doc(db, 'Users', userID, 'backtesting', currentSessionId, 'trades', `Trade ${trade.tradeNumber}`);
    
    const updates = {
      stopLoss: newStopLoss,
      lastTrailingUpdate: currentPrice,
      lastTrailingUpdateTime: playbackTime
    };

    await updateDoc(tradeRef, updates);

    // Update local state
    setTradeHistory(prev => prev.map(t => 
      t.tradeNumber === trade.tradeNumber 
        ? { ...t, ...updates }
        : t
    ));

    console.log('Trailing stop updated successfully');
  } catch (error) {
    console.error('Error updating trailing stop:', error);
  }
};

const closeTradeAtStop = async (trade, stopPrice, playbackTime) => {
  try {
    const profitLoss = await calculateProfitLoss(
      stopPrice,
      trade.size,
      trade.side,
      userID,
      currentSessionId,
      trade.tradeNumber
    );

    await saveTradeToFirestore({
      ...trade,
      status: 'Closed',
      exitPrice: stopPrice,
      profitLoss,
      exitDate: playbackTime,
      closedByTrailingStop: true
    }, userID, currentSessionId, playbackTime);

    console.log(`Closed trade ${trade.tradeNumber} at trailing stop: ${stopPrice}`);
  } catch (error) {
    console.error('Error closing trade at stop:', error);
  }
};

const applyPartialTakeProfit = async (trade, currentPrice) => {
  try {
    console.log('Starting partial take profit:', {
      trade,
      currentPrice,
      symbol: trade.symbol || symbol, // Fix: Use trade.symbol or fallback to current symbol
      lots: trade.size
    });

    if (!trade.partialTakeProfit || !trade.partialTakeProfitPercent) {
      console.error('Missing partial take profit settings');
      return false;
    }

    const partialPercent = trade.partialTakeProfitPercent / 100;
    const partialSize = trade.size * partialPercent;
    const remainingSize = trade.size - partialSize;

    // Generate new trade number for the partial close
    const newTradeNumber = await generateNewTradeNumber();

    // Use the standard calculateProfitLoss function that works for all pairs
    const profitLoss = await calculateProfitLoss(
      currentPrice,
      partialSize,
      trade.side,
      userID, 
      currentSessionId,
      trade.tradeNumber,
      getContractSize(trade.symbol || symbol) // Fix: Use trade.symbol or fallback to current symbol
    );

    console.log('Profit calculation details:', {
      partialSize,
      priceDiff: currentPrice - trade.price,
      profitLoss,
      symbol: trade.symbol || symbol, // Add symbol to logs
      contractSize: getContractSize(trade.symbol || symbol)
    });

    const closedPartialTrade = {
      ...trade,
      size: partialSize,
      status: 'Closed',
      exitPrice: currentPrice,
      profitLoss: profitLoss,
      exitDate: datafeed.currentPlaybackTime,
      currentPlaybackTime: trade.currentPlaybackTime,
      isPartialClose: true,
      originalTradeNumber: trade.tradeNumber,
      tradeNumber: newTradeNumber,
      stopLoss: trade.stopLoss, // Ensure SL and TP are copied
      takeProfit: trade.takeProfit // Ensure SL and TP are copied
    };

    console.log('Closed partial trade:', closedPartialTrade);
  
    const updatedOriginalTrade = {
      ...trade,
      size: remainingSize,
      status: 'Open',
      currentPlaybackTime: trade.currentPlaybackTime,
      stopLoss: trade.stopLoss, // Ensure SL and TP are copied
      takeProfit: trade.takeProfit // Ensure SL and TP are copied
    };

    console.log('Updated original trade:', updatedOriginalTrade);
  
    const batch = writeBatch(db);
  
    const newTradeRef = doc(collection(db, 'Users', userID, 'backtesting', currentSessionId, 'trades'));
    batch.set(newTradeRef, closedPartialTrade);
  
    const originalTradeRef = doc(db, 'Users', userID, 'backtesting', currentSessionId, 'trades', `Trade ${trade.tradeNumber}`);
    batch.update(originalTradeRef, updatedOriginalTrade);
  
    await batch.commit();
    console.log('Batch commit successful');
  
    return true;
  
    setTradeSnackbarMessage(
      `Partial take profit executed: ${trade.partialTakeProfitPercent}% of position closed at ${currentPrice}`
    );
    setTradeSnackbarOpen(true);
  
    return true;

  } catch (error) {
    console.error('Error executing partial take profit:', error);
    setTradeSnackbarMessage('Failed to execute partial take profit');
    setTradeSnackbarOpen(true);
    return false;
  }
};
  
  const generateNewTradeNumber = async () => {
    // Fetch the latest trade number from Firestore or maintain a local counter
    // Example using Firestore to get the highest trade number
    const tradesRef = collection(db, 'Users', userID, 'backtesting', currentSessionId, 'trades');
    const tradesSnapshot = await getDocs(tradesRef);
    const tradeNumbers = tradesSnapshot.docs.map(doc => doc.data().tradeNumber);
    const maxTradeNumber = Math.max(...tradeNumbers);
    return maxTradeNumber + 1;
  };  
  const partialTakeProfit = (tradeId, partialSize) => {
    setTradeHistory(prevTradeHistory => {
      return prevTradeHistory.map(trade => {
        if (trade.id === tradeId && trade.status === 'open') {
          const currentPrice = fetchCurrentCandlePrice(symbol, currentPlaybackTime);
          const partialProfitLoss = calculateProfitLoss({ ...trade, size: partialSize }, currentPrice);
          trade.size -= partialSize;
          trade.profitLoss += partialProfitLoss;
          return trade;
        }
        return trade;
      });
    });
  };

  // Example listener for updating `currentPrice` on each candle tick
  const updateCandlePrice = (newPrice) => {
    setCurrentPrice(newPrice);
  };

  
  // Updating Trade History
  const updateTradeHistory = useCallback((trade) => {
    setTradeHistory(prevTradeHistory => {
      return prevTradeHistory.map(t => t.tradeNumber === trade.tradeNumber ? { ...t, unrealizedProfit: trade.unrealizedProfit } : t);
    });
  }, []);




  const displayNotification = useCallback((message) => {
    setNotifications(prevNotifications => [...prevNotifications, message]);
  }, []);

  const exportData = useCallback(() => {
    // Logic for exporting data as CSV
  }, []);


  const handleBlur = () => {
    if (displayStopLoss !== null && !isNaN(displayStopLoss)) {
      setDisplayStopLoss(parseFloat(displayStopLoss).toFixed(2));
    }
  };
  
  const generateReport = useCallback(() => {
    // Logic for generating a summary report
  }, []);

  useEffect(() => {
    if (!useStopLossTakeProfit) {
      setStopLoss(0);
      setTakeProfit(0);
    }
  }, [useStopLossTakeProfit]);

  const saveBacktest = useCallback(async () => {
    try {
      const docRef = doc(db, 'Users', userID, 'backtesting', 'session');
      await setDoc(docRef, {
        tradeHistory,
        performanceStats,
        settings,
      });
      displayNotification('Backtest saved successfully.');
    } catch (error) {
      console.error('Error saving backtest:', error);
      displayNotification('Error saving backtest.');
    }
  }, [userID, tradeHistory, performanceStats, settings, displayNotification]);

  const deleteBacktest = useCallback(async () => {
    try {
      const docRef = doc(db, 'Users', userID, 'backtesting', 'session');
      await deleteDoc(docRef);
      displayNotification('Backtest deleted successfully.');
    } catch (error) {
      console.error('Error deleting backtest:', error);
      displayNotification('Error deleting backtest.');
    }
  }, [userID, displayNotification]);

  // Load session data when the session is active
  useEffect(() => {
    const loadSessionData = async () => {
      if (!userID || !currentSessionId) {
        console.error('userID or currentSessionId is undefined.');
        return;
      }

      const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        setAccountBalance(parseFloat(sessionData.accountBalance) || 0); // Set account balance
        setIsSessionActive(true);
      } else {
        console.error('Session document does not exist.');
      }
    };

    loadSessionData();
  }, [userID, currentSessionId]);

  const loadBacktest = useCallback(async () => {
    try {
      const docRef = doc(db, 'Users', userID, 'backtesting', 'session');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const { tradeHistory, performanceStats, settings } = docSnap.data();
        setTradeHistory(tradeHistory);
        setPerformanceStats(performanceStats);
        setSettings(settings);
        displayNotification('Backtest loaded successfully.');
      } else {
        displayNotification('No backtest found.');
      }
    } catch (error) {
      console.error('Error loading backtest:', error);
      displayNotification('Error loading backtest.');
    }
  }, [userID, displayNotification]);

  const handleSessionAction = async (e, session) => {
    const action = e.target.value;
    if (action === 'load') {
      // Load the session
      setNewSession(session);
      setSymbol(session.symbol);
      setStartDate(session.startDate);
      setEndDate(session.endDate);
      // Load other session details as needed
      setIsSessionActive(true);
    } else if (action === 'delete') {
      // Delete the session
      try {
        await deleteDoc(doc(db, 'Users', userID, 'backtesting', session.id));
        // Remove the session from the state
        setSessions(sessions.filter(s => s.id !== session.id));
      } catch (error) {
        console.error('Error deleting session:', error);
      }
    }
  };
  
  const sortedTradeHistory = tradeHistory.sort((a, b) => {
    // Sort by status: Open and Pending trades first
    if (a.status === 'Closed' && (b.status === 'Open' || b.status === 'Pending')) return 1;
    if ((a.status === 'Open' || a.status === 'Pending') && b.status === 'Closed') return -1;
  
    // If both trades have the same status, sort by trade number in descending order
    return b.tradeNumber - a.tradeNumber;
  });
  

const customizeSettings = useCallback((newSettings) => {
    setSettings((prevSettings) => ({
        ...prevSettings,
        ...newSettings,
    }));
}, []);


const showTradeSnackbar = (message) => {
  setTradeSnackbarMessage(message);
  setTradeSnackbarOpen(true);
};

  const slicedData = useMemo(() => data.slice(0, currentCandleIndex + 1), [data, currentCandleIndex]);
  const openTrades = useMemo(() => {
    const tradeIds = new Set();
    return tradeHistory.filter(trade => {
      if (trade.status === 'Open' && !tradeIds.has(trade.id)) {
        tradeIds.add(trade.id);
        return true;
      }
      return false;
    });
  }, [tradeHistory])
  const memoizedChart = useMemo(() => (
    <TradingViewChart symbol={symbol} data={data.slice(0, currentCandleIndex + 1)} settings={settings} onSettingChange={customizeSettings} />
  ), [symbol, data, currentCandleIndex, settings]);

  return (
    <Card>
      <MDBox
        variant="gradient"
        bgColor="light"
        color="dark"
        coloredShadow="light"
        borderRadius="xl"
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="4rem"
        height="4rem"
        mt={-3}
        ml={2} // Add left margin
      >
        <Avatar variant="rounded">
          <WaterfallChartIcon fontSize="large" color="dark" />
        </Avatar>
      </MDBox>      
      <MDBox p={3}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    <MDTypography variant="h5">
      FX Bullsheet Backtesting
    </MDTypography>
    {isSessionActive && (
    <MDTypography 
      variant="caption" 
      color="textSecondary" 
      sx={{ 
        position: 'absolute', 
        top: 95, 
        left: '15%', 
        zIndex: 5, 
        backgroundColor: 'rgba(0, 0, 0, 0.5)', 
        padding: '2px 6px', 
        borderRadius: '4px' 
      }}
    >
      Please be patient with load times for initial loading of 1m intervals. Thank you!
    </MDTypography>
  )}
    {isSessionActive && (
      <MDButton 
          variant="gradient" 
          color="success" 
          onClick={() => handleEndSession(true)}
      >
          Save & Exit Session
      </MDButton>
    )}
    {isSessionActive && (
          <Box mt={2}>
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => setOpenChallengeModal(true)}
            >
              Start Prop Firm Challenge
            </MDButton>
          </Box>
        )}
  </Box>
  
  <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{ '& .MuiPaper-root': { backgroundColor: theme.palette.background.default || '#bac7db' } }}
        >
          <DialogTitle id="alert-dialog-title" sx={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
            <MDTypography variant="h6">
            {"Are you sure you want to delete this session?"}
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
            <DialogContentText id="alert-dialog-description">
            <MDTypography variant="body2">
              This action is not reversible! Are you sure you want to delete the session "{selectedSession?.sessionName}"?
            </MDTypography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <MDButton onClick={handleDialogClose} color="info">
              Cancel
            </MDButton>
            <MDButton onClick={handleDeleteSession} color="error" autoFocus disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} /> : 'Delete'}
            </MDButton>
            </Box>
          </DialogActions>
        </Dialog>
  {!isSessionActive && (
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6}>
            <MDButton variant="contained" color="warning" onClick={handleOpenNewSessionModal}>
              Start New Backtesting Session
            </MDButton>
          </Grid>
        </Grid>
      )}
      {!isSessionActive && (
        <Box mt={2}>
          <MDTypography variant="h6">Previous Sessions:</MDTypography>
          {sessions.length === 0 ? (
            <MDTypography variant="body1">
              No previous sessions available.
            </MDTypography>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' }}>
                    <MDTypography variant="h6">Session Name</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">Start Date</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">End Date</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">Symbol</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">Account Balance</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">Strategy</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">Profit/Loss</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}>
                    <MDTypography variant="h6">Created On</MDTypography>
                  </th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', borderTopRightRadius: '12px', borderBottomRightRadius: '12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice().sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)).map((session, index) => (
                  <tr key={index}>
                    <td><MDTypography variant="body3">{session.sessionName}</MDTypography></td> {/* Display the session name */}
                    <td><MDTypography variant="body3">{new Date(session.startDate).toLocaleDateString()}</MDTypography></td>
                    <td><MDTypography variant="body3">{new Date(session.endDate).toLocaleDateString()}</MDTypography></td>
                    <td><MDTypography variant="body3">{session.symbol}</MDTypography></td>
                    <td><MDTypography variant="body3">${parseFloat(session.accountBalance).toFixed(2)}</MDTypography></td>
                    <td><MDTypography variant="body3">{session.strategy || 'N/A'}</MDTypography></td>
                    <td><MDTypography variant="body3">{session.totalPnL != null ? `$${session.totalPnL.toFixed(2)}` : 'N/A'}</MDTypography></td>
                    <td><MDTypography variant="body3">{new Date(session.createdOn).toLocaleString()}</MDTypography></td>
                    <td>
                      <IconButton onClick={(e) => handleMenuOpen(e, session)} sx={{ color: iconColor }}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={() => handleMenuAction('load')}>Load</MenuItem>
                        <MenuItem onClick={() => handleMenuAction('delete')}>Delete</MenuItem>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
          )}
        </Box>
      )}
            {isSessionActive && (
        <>
          <Divider />
              {/* Confirmation Dialog */}

  

            
            <Modal open={isOrderModalOpen} onClose={() => handleIsOrderModalOpen(false)}>
              <Box sx={{ ...modalStyle, padding: '20px', borderRadius: '10px', width: '500px', backgroundColor: theme.palette.background.default || '#bac7db', maxHeight: '90vh', // Set max height to 90% of viewport height
          overflowY: 'auto',  }}>
                <MDTypography variant="h4">New Order</MDTypography>


                {/* Toggle for Stop Loss and Take Profit */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <MDTypography variant="caption">
                    {useStopLossTakeProfit ? "Skip Stop Loss and Take Profit" : "Enable Stop Loss and Take Profit"}
                  </MDTypography>
                  <Switch
                    checked={useStopLossTakeProfit}
                    onChange={() => {
                      setUseStopLossTakeProfit(prev => {
                        const newValue = !prev;
                        if (!newValue) {
                          setStopLoss(0);
                          setTakeProfit(0);
                        }
                        return newValue;
                      });
                    }}
                    name="useStopLossTakeProfitToggle"
                    inputProps={{ 'aria-label': 'toggle stop loss and take profit' }}
                  />
                </Box>

                {useStopLossTakeProfit && (
                  <>
                    {/* Risk Percent Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <MDTypography variant="caption">Pre-Set Risk Percent</MDTypography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {[0.5, 1, 1.5, 2, 2.5, 3].map((value) => (
                        <MDButton 
                          key={value} 
                          variant="outlined" 
                          onClick={() => {
                            setRiskPercent(value);
                            handleRiskAndRewardUpdate(); // Update calc
                          }}
                          sx={{ backgroundColor: theme.palette.background.primary || '#bac7db', color: theme.palette.text.primary }}
                        >
                          {value}%
                        </MDButton>
                      ))}
                    </Box>
                    <TextField
  label="Custom Risk %"
  type="number"
  value={useManualTpSl ? (customRiskAmount !== null ? customRiskAmount : '') : riskPercent}
  onChange={(e) => {
    const value = e.target.value === '' ? null : Number(e.target.value);
    if (useManualTpSl) {
      setCustomRiskAmount(value);
    } else {
      setRiskPercent(value);
    }
    handleRiskAndRewardUpdate();
  }}
  fullWidth
  sx={{ mt: 2 }}
/>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <MDTypography variant="caption" sx={{ color: theme.palette.error.main }}>
                        Estimated Loss: ${typeof estimatedLoss === 'number' ? estimatedLoss.toFixed(2) : 'Loading...'}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: theme.palette.success.main }}>
                        Estimated Profit: ${typeof estimatedProfit === 'number' ? estimatedProfit.toFixed(2) : 'Loading...'}
                      </MDTypography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <MDTypography variant="caption">Risk-Reward Target</MDTypography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {[1, 1.5, 2, 2.5, 3, 3.5].map((value) => (
                        <MDButton 
                          key={value} 
                          variant="outlined" 
                          sx={{ backgroundColor: theme.palette.background.primary || '#bac7db', color: theme.palette.text.primary }}
                          onClick={() => {
                            setRrTarget(value);
                            handleRiskAndRewardUpdate(); // Update calc
                          }}
                        >   
                          1:{value}
                        </MDButton>
                      ))}
                    </Box>
                    <TextField
                      label="Custom Risk-Reward"
                      type="number"
                      value={useManualTpSl ? customRrTarget : rrTarget}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (useManualTpSl) {
                          setCustomRrTarget(value);
                        } else {
                          setRrTarget(value);
                        }
                        handleRiskAndRewardUpdate(); // Update calc
                      }}
                      fullWidth
                      sx={{ mt: 2 }}
                    />
                  </>
                )}

                {/* Toggle for Lots vs Dollar */}
                <MDTypography variant="caption">Input Mode:</MDTypography>
                <Tooltip title="Dollar value coming soon">
                  <span>
                    <Switch
                      checked={false} // Always set to false for dollar amount
                      disabled // Disable the switch
                      name="useLotsToggle"
                      inputProps={{ 'aria-label': 'toggle between lots and dollar amount' }}
                    />
                  </span>
                </Tooltip>
                <MDTypography variant="caption">Lots</MDTypography>

                {/* Amount Field */}
                <TextField
                  label={useLots ? "Amount (Lots) *" : "Amount ($) *"}
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value === '' ? '' : Number(e.target.value));
                    handleRiskAndRewardUpdate(); // Trigger calc
                  }}
                  error={amountError}
                  helperText={amountError ? "Amount is required" : ""} 
                  fullWidth
                  sx={{ mt: 2 }}
                />
                {/* Toggle for Manual TP & SL */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <MDTypography variant="caption">Auto TP & SL</MDTypography>
                  <Switch
                    checked={useManualTpSl}
                    onChange={() => {
                      setUseManualTpSl(!useManualTpSl);
                      setStopLoss(null); // Clear stop loss value
                      setTakeProfit(null); // Clear take profit value
                      setCustomStopLoss(null); // Clear custom stop loss value
                      setCustomTakeProfit(null); // Clear custom take profit value
                      handleRiskAndRewardUpdate(); // Update calc based on toggle
                    }}
                    name="manualTpSlToggle"
                    inputProps={{ 'aria-label': 'toggle between manual and auto TP & SL' }}
                  />
                  <MDTypography variant="caption">Manual TP & SL</MDTypography>
                </Box>

                {useStopLossTakeProfit && (
                  <>
                  <TextField
                    label="Stop Loss"
                    type="number"
                    value={stopLoss !== null ? stopLoss: ''}
                    onChange={(e) => {
                      const customSL = e.target.value === '' ? null : parseFloat(e.target.value);
                      setStopLoss(customSL);
                      setCustomStopLoss(customSL); // Set custom stop loss
                      handleRiskAndRewardUpdate(); // Manual update with external calc
                    }}
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={{ step: "0.00001" }} // Allow decimal values
                  />
                  <TextField
                    label="Take Profit"
                    type="number"
                    value={takeProfit !== null ? takeProfit : ''}
                    onChange={(e) => {
                      const customTP = e.target.value === '' ? null : parseFloat(e.target.value);
                      setTakeProfit(customTP);
                      setCustomTakeProfit(customTP); // Set custom take profit
                      handleRiskAndRewardUpdate(); // Manual update with external calc
                    }}
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={{ step: "0.00001" }} // Allow decimal values
                  />
                </>
                )}

                {/* Side and Type Fields */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <MDTypography variant="caption">Order Side: Sell</MDTypography>
                  <Switch
                    checked={orderSide === "buy"}
                    onChange={() => {
                      setOrderSide(orderSide === "buy" ? "sell" : "buy");
                      handleRiskAndRewardUpdate(); // Update calc
                    }}
                    name="orderSideToggle"
                    inputProps={{ 'aria-label': 'toggle between buy and sell' }}
                    sx={{ border: orderSideError ? '1px solid red' : 'none' }}
                  />
                  <MDTypography variant="caption">Buy</MDTypography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <MDTypography variant="caption">Order Type: Limit Order</MDTypography>
                  <Switch
                    checked={orderType === "market"}
                    onChange={() => {
                      setOrderType(orderType === "market" ? "limit" : "market");
                      handleRiskAndRewardUpdate(); // Update calc
                    }}
                    name="orderTypeToggle"
                    inputProps={{ 'aria-label': 'toggle between market and limit order' }}
                    sx={{ border: orderTypeError ? '1px solid red' : 'none' }} 
                  />
                  <MDTypography variant="caption">Market Order</MDTypography>
                </Box>

                {/* Conditional Entry Price for Limit Orders */}
                {orderType === 'limit' && (
                  <TextField
                    label="Entry Price *"
                    type="number"
                    value={entryPrice}
                    onChange={(e) => {
                      setEntryPrice(Number(e.target.value));
                      handleRiskAndRewardUpdate(); // Update calc
                    }}
                    fullWidth
                    placeholder="0.00" // Placeholder text
                    sx={{ mt: 2 }}
                    error={entryPriceError}
                    helperText={entryPriceError ? "Entry price is required for limit orders" : ""}
                  />
                )}

                {/* Save and Cancel Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <MDButton variant="contained" color="success" onClick={() => placeOrder(orderType, entryPrice, amount, calculatedSL,useManualTpSl ? customRiskAmount : riskPercent, calculatedTP, riskPercent, rrTarget, customStopLoss, customTakeProfit, )}>
                    Create Backtesting Order
                  </MDButton>
                  <MDButton variant="outlined" color="error" onClick={() => handleIsOrderModalOpen(false)}>
                    Cancel
                  </MDButton>
                </Box>
                <Snackbar
                  open={showSnackbar}
                  autoHideDuration={2000}
                  onClose={() => setShowSnackbar(false)}
                  message="Please fill out all required fields."
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />
              </Box>
            </Modal>
          </>
        )} 
        {isSessionActive && (
          <Box display="flex" position="relative">
            <Box flexGrow={1} sx={{ transition: 'flex-grow 0.3s' }} flexBasis={isPanelOpen ? 'calc(100% - 300px)' : '100%'}>
              <TradingViewChart
                symbol={symbol}
                settings={settings}
                data={data.slice(0, currentCandleIndex + 1)}
                startDate={startDate}
                endDate={endDate}
                startDateMillis={newSession.startDateMillis}
                endDateMillis={newSession.endDateMillis}
                onSettingChange={customizeSettings}
                sessionId={currentSessionId}
                userID={userID}
                onDatafeedReady={handleDatafeedReady}
                trades={tradeHistory}
                updateTrades={setTradeHistory}
                onSaveLayout={saveChartLayout}
                onLoadLayout={loadLayout}
                handleIsOrderModalOpen={handleIsOrderModalOpen}
                onDrawingToolClick={handleDrawingToolClick}
                applyTrailingStopLoss={applyTrailingStopLoss}
                contractSize={getContractSize(symbol)}
                isDemo={isDemo}
                setTradeSnackbarMessage={setTradeSnackbarMessage} // Add these
                setTradeSnackbarColor={setTradeSnackbarColor}
                setTradeSnackbarOpen={setTradeSnackbarOpen}
              />
            </Box>
            <Box sx={{ width: isPanelOpen ? '250px' : '0px', transition: 'width 0.3s', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <IconButton 
        onClick={() => setIsPanelOpen(!isPanelOpen)} 
        sx={{ 
          position: 'absolute', 
          left: '-40px', 
          top: '50%', 
          transform: 'translateY(-50%)', 
          transition: 'left 0.3s', 
          zIndex: 1, 
          color: iconColor 
        }}
      >
        {isPanelOpen ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
      </IconButton>
      <Collapse in={isPanelOpen} orientation="horizontal" sx={{ width: '100%', transition: 'width 0.3s' }}>
        <Box 
          p={2} 
          bgcolor={theme.palette.background.default}
          borderRadius="12px" 
          boxShadow={1}
        >
          <PropFirmChallenge
                  userID={userID}
                  currentSessionId={currentSessionId}
                  tradeHistory={tradeHistory}
                  accountBalance={accountBalance}
                  unrealizedPnL={unrealizedPnL}
                  currentPlaybackTime={currentPlaybackTime}
                  isPanelOpen={isPanelOpen}
                  setSelectedChallenges={setSelectedChallenges}
                  openChallengeModal={openChallengeModal} // Pass new prop
                  onCloseChallengeModal={handleCloseChallengeModal} // Pass close handler
                  onOpenStageDialog={handleOpenStageDialog}
                />
          <MDTypography 
            variant="h6" 
            mb={2}
            sx={{ color: iconColor }} // Using iconColor
          >
            Open Trades
          </MDTypography>
          {openTrades.length === 0 ? (
            <MDTypography 
              variant="body2" 
              sx={{ color: iconColor }} // Using iconColor
            >
              No open trades currently
            </MDTypography>
          ) : (
            openTrades.map(trade => (
              <Box 
                key={trade.id} 
                mb={2} 
                p={2} 
                borderRadius="12px"
                bgcolor={theme.palette.background.default}
                boxShadow={`0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'}`}
                border="1px solid"
                borderColor={theme.palette.divider}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box 
                    sx={{ 
                      borderRadius: '12px', 
                      px: 1.5, 
                      py: 0.5,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(33, 150, 243, 0.3)' 
                        : 'rgba(33, 150, 243, 0.2)',
                      border: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(33, 150, 243, 0.5)' 
                        : 'rgba(33, 150, 243, 0.4)'}`,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <MDTypography 
                      variant="caption" 
                      fontWeight="bold"
                      sx={{ color: iconColor }} // Using iconColor
                    >
                      #{trade.tradeNumber}
                    </MDTypography>
                    <MDTypography 
                      variant="caption" 
                      sx={{ color: iconColor }} // Using iconColor
                    >
                      {trade.status}
                    </MDTypography>
                  </Box>
                  <IconButton 
                    onClick={(e) => handleTradeMenuOpen(e, trade)} 
                    sx={{ color: iconColor }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography 
                    variant="body2" 
                    sx={{ color: iconColor }} // Using iconColor
                  >
                    Entry:
                  </MDTypography>
                  <MDTypography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ color: iconColor }} // Using iconColor
                  >
                    {trade.price}
                  </MDTypography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography 
                    variant="body2" 
                    sx={{ color: iconColor }} // Using iconColor
                  >
                    Stop Loss:
                  </MDTypography>
                  <MDTypography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ color: iconColor }} // Using iconColor
                  >
                    {trade.stopLoss.toFixed(5)}
                  </MDTypography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <MDTypography 
                    variant="body2" 
                    sx={{ color: iconColor }} // Using iconColor
                  >
                    Take Profit:
                  </MDTypography>
                  <MDTypography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ color: iconColor, }} // Using iconColor
                  >
                    {trade.takeProfit.toFixed(5)}
                  </MDTypography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <MDTypography 
                    variant="body2" 
                    sx={{ color: iconColor, mr: 1 }} // Using iconColor
                  >
                    Unrealized PnL:
                  </MDTypography>
                  <Box 
                    sx={{ 
                      borderRadius: '12px', 
                      px: 1.5, 
                      py: 0.5,
                      backgroundColor: trade.unrealizedProfit > 0 
                        ? theme.palette.mode === 'dark' 
                          ? 'rgba(76, 175, 80, 0.3)' 
                          : 'rgba(76, 175, 80, 0.15)'
                        : theme.palette.mode === 'dark' 
                          ? 'rgba(244, 67, 54, 0.3)' 
                          : 'rgba(244, 67, 54, 0.15)',
                      border: `1px solid ${trade.unrealizedProfit > 0 
                        ? theme.palette.mode === 'dark' 
                          ? 'rgba(76, 175, 80, 0.5)' 
                          : 'rgba(76, 175, 80, 0.3)'
                        : theme.palette.mode === 'dark' 
                          ? 'rgba(244, 67, 54, 0.5)' 
                          : 'rgba(244, 67, 54, 0.3)'}`,
                    }}
                  >
                <MDTypography 
                  variant="body2" 
                  fontWeight="medium"
                  sx={{ 
                    color: trade.unrealizedProfit > 0 
                      ? theme.palette.mode === 'dark' 
                        ? '#81c784' // Lighter green for dark mode
                        : '#2e7d32' 
                      : theme.palette.mode === 'dark' 
                        ? '#ef5350' // Lighter red for dark mode
                        : '#c62828'
                  }}
                >
                  ${trade.unrealizedProfit?.toFixed(2)}
                </MDTypography>
              </Box>
            </Box>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedTrade === trade}
                  onClose={handleTradeMenuClose}
                  PaperProps={{
                    sx: {
                      backgroundColor: theme.palette.background.paper,
                      color: iconColor, // Match menu text to iconColor
                    }
                  }}
                >
                  <MenuItem onClick={() => handleTradeMenuAction('edit', trade)}>
                    <MDTypography variant="body2" sx={{ color: iconColor }}>
                      Edit Trade
                    </MDTypography>
                  </MenuItem>
                  <MenuItem onClick={() => handleTradeMenuAction('close', trade)}>
                    <MDTypography variant="body2" sx={{ color: iconColor }}>
                      Close Trade
                    </MDTypography>
                  </MenuItem>
                </Menu>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  </Box>
)}


        {isSessionActive && (
          <Box mt={1} display="flex" justifyContent="center" alignItems="center" p={2} bgcolor={theme.palette.background.default || '#bac7db'} borderRadius="12px" boxShadow={1} gap={4}>
            <MDTypography variant="h6">
              Account Balance: ${typeof accountBalance === 'number' ? accountBalance.toFixed(2) : 'N/A'}
            </MDTypography>
            <MDTypography variant="h6">
              Realized PnL: ${realizedPnL.toFixed(2)}
            </MDTypography>
            <MDTypography variant="h6">
              Unrealized PnL: ${parseFloat(totalUnrealizedPnL).toFixed(2)}
            </MDTypography>
          </Box>
        )}        
{isSessionActive && (
  <Box mt={3} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
    <MDTypography variant="h5">Trade History</MDTypography>
    
    {/* Add this wrapper with horizontal scrolling */}
    <Box sx={{ 
      width: '100%', 
      overflowX: 'auto',
      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
      borderRadius: '12px',
      '&::-webkit-scrollbar': {
        height: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
        borderRadius: '10px',
      },
    }}>
      <table style={{ 
        borderCollapse: 'separate', 
        borderSpacing: '0 8px',
        width: '100%',
        minWidth: '1200px', // Set a minimum width for the table
      }}>
              <thead>
              <tr>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' }}><MDTypography variant="h6">Trade #</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Status</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Date Open</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Date Close</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Side</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Entry Price</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Exit Price</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Stop Loss</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Take Profit</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Risk/Reward</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Position Size</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db' }}><MDTypography variant="h6">Profit/Loss</MDTypography></th>
                  <th style={{ backgroundColor: theme.palette.background.default || '#bac7db', borderTopRightRadius: '12px', borderBottomRightRadius: '12px' }}><MDTypography variant="h6"></MDTypography></th>
                </tr>
              </thead>
              <tbody>
                {sortedTradeHistory.map((trade, index) => {
                  const isProfit = trade.profitLoss > 0;
                  const isManualClose = trade.manualClose;
                  const isPartialClose = trade.isPartialClose;
                  const isParentOfPartialClose = sortedTradeHistory.some(t => 
                    t.isPartialClose && t.originalTradeNumber === trade.tradeNumber
                  );
                                    

                  // Calculate the max RR
                  // Update the maxRR calculation to handle both entryPrice and price fields
                  let maxRR = '-';
                  if ((trade.entryPrice || trade.price) && trade.stopLoss && trade.takeProfit) {
                    // Use entryPrice if available, otherwise use price
                    const entryPrice = trade.entryPrice || trade.price;
                    
                    // Always calculate RR if we have all required values
                    const potentialReward = trade.side === 'Buy'
                      ? trade.takeProfit - entryPrice
                      : entryPrice - trade.takeProfit;
                    
                    const risk = trade.side === 'Buy'
                      ? entryPrice - trade.stopLoss
                      : trade.stopLoss - entryPrice;

                    // Check for risk-free condition first
                    if ((trade.side === 'Buy' && trade.stopLoss >= entryPrice) || 
                        (trade.side === 'Sell' && trade.stopLoss <= entryPrice)) {
                      maxRR = 'RF';
                    } else {
                      // Calculate RR for all other cases including manual closes
                      const rrValue = potentialReward / risk;
                      maxRR = rrValue < 0 ? `-${Math.abs(rrValue).toFixed(2)}` : rrValue.toFixed(2);
                    }
                  }

                  return (
                    <tr
                      key={index}
                      style={{
                        fontStyle: isPartialClose ? 'italic' : 'normal',
                      }}
                      title={isPartialClose ? `Partial close of Trade #${trade.originalTradeNumber}` : 
                      trade.isBreakEven ? 'Trade closed at break-even' :
                      trade.trailingStopLoss ? `Trade using trailing stop loss (${
                        trade.trailingStopLossType === 'percentage' ? `${trade.trailingStopLossValue}%` :
                        trade.trailingStopLossType === 'pips' ? `${trade.trailingStopLossValue} pips` :
                        `${trade.trailingStopLossValue} price units`
                      })` : ''}
                    >
                      {/* First cell - contains status indicator pill */}
                      <td style={{ textAlign: 'center' }}>
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', // Add justify content center
    gap: 1,
    width: '100%' // Ensure the Box takes full width of the cell
  }}>
    {trade.status === 'Closed' && (
      <Box 
        sx={{ 
          borderRadius: '12px', 
          px: 1.5, 
          py: 0.5,
          minWidth: '90px', // Increased width to fit trade number
          textAlign: 'center', 
          display: 'inline-block',
          background: trade.isBreakEven 
            ? 'linear-gradient(90deg, rgba(255,165,0,0.2) 0%, rgba(255,165,0,0.7) 100%)' 
            : isProfit 
              ? 'linear-gradient(90deg, rgba(100,200,100,0.2) 0%, rgba(100,200,100,0.7) 100%)' 
              : 'linear-gradient(90deg, rgba(200,100,100,0.2) 0%, rgba(200,100,100,0.7) 100%)',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
          border: `1px solid ${trade.isBreakEven 
            ? 'rgba(255,165,0,0.3)' 
            : isProfit 
              ? 'rgba(100,200,100,0.3)' 
              : 'rgba(200,100,100,0.3)'}`,
        }}
      >
        <MDTypography 
          variant="caption" 
          fontWeight="bold"
          sx={{ 
            color: trade.isBreakEven 
              ? theme.palette.mode === 'dark' ? 'rgba(255, 190, 90, 0.9)' : 'rgba(7, 7, 7, 0.9)' 
              : isProfit 
                ? theme.palette.mode === 'dark' ? 'rgba(120, 255, 120, 0.9)' : 'rgba(15, 15, 15, 0.9)' 
                : theme.palette.mode === 'dark' ? 'rgba(255, 120, 120, 0.9)' : 'rgba(8, 8, 8, 0.9)',
          }}
        >
          #{trade.tradeNumber} {/* Added trade number here */}
          {' '}
          {trade.isBreakEven 
            ? 'BE' 
            : isProfit 
              ? 'PROFIT' 
              : 'LOSS'}
        </MDTypography>
      </Box>
    )}
    {trade.status !== 'Closed' && (
      <MDTypography variant="body2">
        {trade.tradeNumber}
      </MDTypography>
    )}
    {isPartialClose && (
      <MDTypography component="span" variant="body2" sx={{ fontSize: '0.8em', marginLeft: '4px' }}>
        (P)
      </MDTypography>
    )}
    {trade.isBreakEven && trade.status !== 'Closed' && (
      <MDTypography component="span" variant="body2" sx={{ fontSize: '0.8em', marginLeft: '4px' }}>
        (BE)
      </MDTypography>
    )}
    {trade.trailingStopLoss && (
      <MDTypography component="span" variant="body2" sx={{ fontSize: '0.8em', marginLeft: '4px' }}>
        (TS)
      </MDTypography>
    )}                          
    {isParentOfPartialClose && (
      <MDTypography component="span" variant="body2" sx={{ fontSize: '0.8em', marginLeft: '4px' }}>
        (Split)
      </MDTypography>
    )}
  </Box>
</td>
                  
                      {/* Status Cell */}
                      <td>
  <Box 
    sx={{ 
      borderRadius: '12px', 
      px: 1.5, 
      py: 0.5,
      minWidth: '80px', // Set minimum width for consistent sizing
      textAlign: 'center', // Center the text for better appearance 
      backgroundColor: trade.status === 'Open' 
        ? 'rgba(33, 150, 243, 0.2)' // Slightly stronger background
        : trade.status === 'Pending' 
          ? 'rgba(255, 193, 7, 0.2)' 
          : 'rgba(158, 158, 158, 0.2)',
      display: 'inline-block',
      border: `1px solid ${trade.status === 'Open' 
        ? 'rgba(33, 150, 243, 0.4)' // Stronger border
        : trade.status === 'Pending' 
          ? 'rgba(255, 193, 7, 0.4)' 
          : 'rgba(158, 158, 158, 0.4)'}`
    }}
  >
    <MDTypography 
      variant="caption" 
      sx={{ 
        color: trade.status === 'Open' 
          ? '#050505' // Darker blue for better readability
          : trade.status === 'Pending' 
            ? '#b35c00' // Darker orange
            : '#212121', // Darker grey for Closed status
        fontWeight: 'medium',
        fontSize: '0.75rem' // Consistent font size
      }}
    >
      {trade.status}
    </MDTypography>
  </Box>
</td>
                  
                      {/* Regular cells */}
                      <td><MDTypography variant="body2">{new Date(trade.currentPlaybackTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</MDTypography></td>
                      <td><MDTypography variant="body2">{trade.exitDate ? new Date(trade.exitDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '-'}</MDTypography></td>
                      <td>
                        <Box 
                          sx={{ 
                            borderRadius: '12px', 
                            px: 1.5, 
                            py: 0.5, 
                            backgroundColor: trade.side === 'Buy' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                            display: 'inline-block',
                            border: `1px solid ${trade.side === 'Buy' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`
                          }}
                        >
                          <MDTypography 
                            variant="caption" 
                            sx={{ 
                              color: trade.side === 'Buy' ? '#2e7d32' : '#c62828',
                              fontWeight: 'medium'
                            }}
                          >
                            {trade.side}
                          </MDTypography>
                        </Box>
                      </td>
                      <td><MDTypography variant="body2">{trade.price}</MDTypography></td>
                      <td><MDTypography variant="body2">{trade.exitPrice ? trade.exitPrice.toFixed(5) : '-'}</MDTypography></td>
                      <td><MDTypography variant="body2">{trade.stopLoss.toFixed(5)}</MDTypography></td>
                      <td><MDTypography variant="body2">{trade.takeProfit.toFixed(5)}</MDTypography></td>
                      <td><MDTypography variant="body2">{maxRR}</MDTypography></td>
                      <td><MDTypography variant="body2">{trade.size}</MDTypography></td>
                      <td>
  <Box 
    sx={{ 
      borderRadius: '12px', 
      px: 1.5, 
      py: 0.5,
      minWidth: '90px', // Set minimum width for consistent sizing
      display: 'inline-block',
      textAlign: 'center', // Center the text for better appearance
      backgroundColor: trade.status === 'Open' 
        ? (trade.unrealizedProfit > 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)')
        : (isProfit ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)'),
      border: `1px solid ${trade.status === 'Open'
        ? (trade.unrealizedProfit > 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)')
        : (isProfit ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)')}`,
    }}
  >
    <MDTypography 
      variant="body2" 
      sx={{ 
        color: trade.status === 'Open'
          ? (trade.unrealizedProfit > 0 ? '#2e7d32' : '#c62828')
          : (isProfit ? '#2e7d32' : '#c62828'),
        fontWeight: 'medium'
      }}
    >
      {trade.status === 'Closed' && typeof trade.profitLoss === 'number'
        ? `$${trade.profitLoss.toFixed(2)}`
        : trade.status === 'Open' && typeof trade.unrealizedProfit === 'number'
        ? `$${trade.unrealizedProfit.toFixed(2)}`
        : '-'}
    </MDTypography>
  </Box>
</td>
                  
                      {/* Action buttons */}
                      <td>
                        {trade.status !== 'Closed' && (
                          <>
                            <IconButton onClick={(e) => handleTradeMenuOpen(e, trade)} sx={{ color: iconColor }}>
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl) && selectedTrade === trade}
                              onClose={handleTradeMenuClose}
                            >
                              {trade.status === 'Pending' && [
                                <MenuItem key="edit" onClick={() => handleTradeMenuAction('edit', trade)}>
                                  <MDTypography variant="body2">Edit Trade</MDTypography>
                                </MenuItem>,
                                <MenuItem key="cancel" onClick={() => handleTradeMenuAction('cancel', trade)}>
                                  <MDTypography variant="body2">Cancel Trade</MDTypography>
                                </MenuItem>
                              ]}
                              {trade.status === 'Open' && [
                                <MenuItem key="edit" onClick={() => handleTradeMenuAction('edit', trade)}>
                                  <MDTypography variant="body2">Edit Trade</MDTypography>
                                </MenuItem>,
                                <MenuItem key="close" onClick={() => handleTradeMenuAction('close', trade)}>
                                  <MDTypography variant="body2">Close Trade</MDTypography>
                                </MenuItem>
                              ]}
                            </Menu>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </Box>
            </Box>
          )}
          <Snackbar
            open={showSnackbar}
            autoHideDuration={4000}
            onClose={() => setShowSnackbar(false)}
            message={snackbarMessage}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            ContentProps={{
              style: {
                backgroundColor: snackbarColor || 'defaultColor', // Replace 'defaultColor' with your default color
              },
            }}
          />     
          <Snackbar
  open={tradeSnackbarOpen}
  autoHideDuration={4000}
  onClose={() => setTradeSnackbarOpen(false)}
  message={tradeSnackbarMessage}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
  ContentProps={{
    style: {
      backgroundColor: tradeSnackbarColor === 'success' ? '#4CAF50' : tradeSnackbarColor === 'error' ? '#F44336' : '#2196F3',
    },
  }}
/>     
      
        <Modal open={isEditTradeModalOpen} onClose={handleEditTradeClose}>
          <Box sx={{ ...modalStyle, padding: '20px', borderRadius: '10px', width: '500px', backgroundColor: theme.palette.background.default || '#bac7db', }}>
            <MDTypography variant="h4">Edit Trade</MDTypography>

            {selectedTrade?.status === 'Pending' && (
              <TextField
                label="Entry Price"
                type="number"
                name="price"
                value={editTradeData.price}
                onChange={handleEditTradeChange}
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
            <TextField
              label="Stop Loss"
              type="number"
              name="stopLoss"
              value={editTradeData.stopLoss}
              onChange={handleEditTradeChange}
              fullWidth
              sx={{ mt: 2 }}
            />
            <TextField
              label="Take Profit"
              type="number"
              name="takeProfit"
              value={editTradeData.takeProfit}
              onChange={handleEditTradeChange}
              fullWidth
              sx={{ mt: 2 }}
            />
<Box sx={{ mt: 2 }}>
  {/* Break Even Toggle - Only show if unrealized profit is positive */}
  {selectedTrade && selectedTrade.unrealizedProfit > 0 && (
    <FormControlLabel
      control={
        <Switch
          checked={editTradeData.stopLoss === selectedTrade.price}
          onChange={() => {
            setEditTradeData(prev => ({
              ...prev,
              stopLoss: prev.stopLoss === selectedTrade.price ? 
                selectedTrade.stopLoss : // Revert to original SL if turning off
                selectedTrade.price // Set to entry price if turning on
            }));
          }}
          name="breakEvenStop"
        />
      }
      label={
        <Box display="flex" alignItems="center">
          <MDTypography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            Move Stop Loss to Break Even
          </MDTypography>
          <Tooltip title="Set stop loss to entry price to secure a risk free trade">
            <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
          </Tooltip>
        </Box>
      }
    />
  )}
</Box>            
            <Box sx={{ mt: 2 }}>
              {/* Trailing Stop Loss Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editTradeData.trailingStopLoss}
                    onChange={() => setEditTradeData(prev => ({ 
                      ...prev, 
                      trailingStopLoss: !prev.trailingStopLoss 
                    }))}
                    name="trailingStopLoss"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <MDTypography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      Use Trailing Stop Loss
                    </MDTypography>
                    <Tooltip title="Trailing stop loss automatically adjusts your stop loss as price moves in your favor">
                      <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
                    </Tooltip>
                  </Box>
                }
              />

              {editTradeData.trailingStopLoss && (
                <>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="trailing-stop-type-label">Trailing Stop Type</InputLabel>
              <Select
                labelId="trailing-stop-type-label"
                name="trailingStopLossType"
                value={editTradeData.trailingStopLossType || ''} 
                onChange={handleEditTradeChange}
                label="Trailing Stop Type" // Important for outline cutout
              >
                    <MenuItem value="" disabled>
                      <MDTypography variant="button">Please Select Trailing Stop Type</MDTypography>
                    </MenuItem>
                      <MenuItem value="percentage">
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <MDTypography variant="button">Percentage (%)</MDTypography>
                          <Tooltip title="Trail by percentage of current price">
                            <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
                          </Tooltip>
                        </Box>
                      </MenuItem>
                      <MenuItem value="pips">
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <MDTypography variant="button">Pips</MDTypography>
                          <Tooltip title="Trail by fixed number of pips">
                            <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
                          </Tooltip>
                        </Box>
                      </MenuItem>
                      <MenuItem value="price">
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <MDTypography variant="button">Fixed Price</MDTypography>
                          <Tooltip title="Trail by fixed price distance">
                            <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
                          </Tooltip>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label={
                      <MDTypography variant="button" >
                        {`Trailing Stop Value (${
                          editTradeData.trailingStopLossType === 'percentage' ? '%' :
                          editTradeData.trailingStopLossType === 'pips' ? 'Pips' : 
                          'Price'
                        })`}
                      </MDTypography>
                    }
                    name="trailingStopLossValue"
                    value={editTradeData.trailingStopLossValue}
                    onChange={handleEditTradeChange}
                    type="number"
                    fullWidth
                    margin="normal"
                    helperText={
                      <MDTypography variant="caption" color="text">
                        {editTradeData.trailingStopLossType === 'percentage' ?
                          "Enter percentage to trail (e.g. 1 = 1%)" :
                          editTradeData.trailingStopLossType === 'pips' ?
                          "Enter number of pips to trail (e.g. 10 = 10 pips)" :
                          "Enter price distance to maintain"}
                      </MDTypography>
                    }
                  />
                </>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              {/* Partial Take Profit Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editTradeData.partialTakeProfit}
                    onChange={() => setEditTradeData(prevData => ({ 
                      ...prevData, 
                      partialTakeProfit: !prevData.partialTakeProfit 
                    }))}
                    name="partialTakeProfitToggle"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <MDTypography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      Partial Take Profit
                    </MDTypography>
                    <Tooltip title="Close a portion of your position, leaving the rest to potentially capture more profit">
                      <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
                    </Tooltip>
                  </Box>
                }
              />

              {editTradeData.partialTakeProfit && (
                <TextField
                  label={
                    <Box display="flex" alignItems="center">
                      <MDTypography variant="button">
                        Partial Take Profit %
                      </MDTypography>
                      <Tooltip title="Percentage of position to close at take profit level (e.g. 50 = close half position)">
                        <InfoIcon sx={{ ml: 1, fontSize: 'small' }} />
                      </Tooltip>
                    </Box>
                  }
                  type="number"
                  name="partialTakeProfitPercent"
                  value={editTradeData.partialTakeProfitPercent}
                  onChange={handleEditTradeChange}
                  fullWidth
                  margin="normal"
                  helperText={
                    <MDTypography variant="caption" color="text">
                      Enter percentage of position to close (e.g. 50 = close half position)
                    </MDTypography>
                  }
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <MDButton variant="contained" color="success" onClick={handleEditTradeSubmit}>
                Save Changes
              </MDButton>
              <MDButton variant="outlined" color="error" onClick={handleEditTradeClose}>
                Cancel
              </MDButton>
            </Box>
          </Box>
        </Modal>
        {isSessionActive && (
          <Box mt={3}>
            <MDTypography variant="h6">Notifications</MDTypography>
            <ul>
              {notifications.map((notification, index) => (
                <li key={index}>
            <MDTypography variant="body1">{notification}</MDTypography>
                </li>
              ))}
            </ul>
          </Box>
        )}
{/* 
        {isSessionActive && (
          <Grid container spacing={3} mt={2}>
            <Grid item xs={12} sm={6}>
              <MDButton variant="contained" color="warning" fullWidth onClick={exportData}>
                Export Data
              </MDButton>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDButton variant="contained" color="warning" fullWidth onClick={generateReport}>
                Generate Report
              </MDButton>
            </Grid>
          </Grid>
        )}
*/}
  
        {/* Modal for New Session */}
        <Modal
          open={isNewSessionModalOpen}
          onClose={handleCloseNewSessionModal}
          aria-labelledby="new-session-modal-title"
          aria-describedby="new-session-modal-description"
        >
          <Box sx={{ ...modalStyle }}>
            <MDTypography id="new-session-modal-title" variant="h6" component="h2" mb={2}>
              Start New Backtesting Session
            </MDTypography>
            <TextField
              label="Session Name"
              name="sessionName"
              value={newSession.sessionName}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />          
            <Box mb={2}>
            <TextField
              label="Account Balance In USD"
              name="accountBalance"
              value={newSession.accountBalance}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              type="number"
            />
            </Box>
            <Box mb={1}>
              <Autocomplete
                options={symbols}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Symbol" 
                    name="symbol" 
                    value={newSession.symbol} // Ensure this is a string
                  />
                )}
                value={symbols.find(option => option.label === newSession.symbol) || null} // Use null if no match
                onChange={(event, newValue) => 
                  setNewSession(prevState => ({ 
                    ...prevState, 
                    symbol: newValue ? newValue.label : '' // Set symbol to the label or an empty string
                  }))
                } 
                fullWidth
                margin="normal"
              />
            </Box>  
            <TextField
              label="Start Date"
              name="startDate"
              type="date"
              value={newSession.startDate}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true}}
              inputProps={{ max: maxDate }}
            />
            <TextField
              label="End Date"
              name="endDate"
              type="date"
              value={newSession.endDate}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true}}
              inputProps={{ max: maxDate }}
            />
            <Box mb={2}>
            <TextField
              label="Strategy"
              name="strategy"
              value={newSession.strategy}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            </Box>
            <MDButton variant="contained" color="success" fullWidth sx={{ marginBottom: '20px' }} onClick={handleNewSession}>
              Create Session
            </MDButton>
            <MDButton variant="outlined" color="error" fullWidth onClick={handleCloseNewSessionModal}>
              Cancel Session
            </MDButton>
          </Box>
        </Modal>
      </MDBox>
    </Card>
  );
};

export default FXBacktesting;